import type { fr } from './fr';
import { aiEn } from './ai.en';
import { adminEn } from './admin.en';
import { secretStoreEn } from './secret-store.en';
import { billingEn } from './billing.en';
import { catalogTableEn, glossaryEn } from './catalogue.en';
import { catalogSearchEn, connectionsEn, lineageEn, previewEn, qualityEn } from './donnees.en';
import { galleryEn, socialEn } from './social.en';
import { searchEn } from './search.en';
import { resourceUsageEn } from './usage.en';
import { filePreviewEn } from './apercu-fichier.en';
import { orchestrationEn } from './orchestration.en';
import { referencesEn } from './references.en';
import { workflowEditorEn } from './workflow-editor.en';
import { alertsEn } from './alerts.en';
import { managedWarehouseEn } from './entrepots-geres.en';
import {
  bucketManageEn,
  preferencesEn,
  projectMetricsEn,
  projectSettingsEn,
  warehouseManageEn,
} from './gestes.en';

/** Même arborescence que fr.ts, valeurs libres (chaînes). */
type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

export const en: DeepStrings<typeof fr> = {
  common: {
    appName: 'graal',
    search: 'Search',
    cancel: 'Cancel',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy: select the text by hand.',
    moreActions: 'More actions',
    open: 'Open',
    filter: 'Filter',
    refresh: 'Refresh',
    download: 'Download',
    loading: 'Loading…',
    escape: 'Esc',
    never: 'Never',
    manual: 'Manual',
    selectAll: 'Select all',
    selectRow: 'Select row',
  },
  errorBoundary: {
    title: 'This screen ran into an error',
    description:
      'An unexpected error interrupted the display. Reload the page; if it happens again, send the reference below to support.',
    retry: 'Try again',
    reload: 'Reload the page',
    home: 'Back to home',
  },
  patterns: {
    reference: {
      label: 'Reference:',
      copy: 'Copy the error reference',
      copied: 'Reference copied',
    },
    codes: {
      SECRET_MANAGED: 'This key belongs to a platform-managed bucket: its value cannot be replaced here.',
      SECRET_INVALID: 'The secret is invalid: check the value you entered.',
      INFRASTRUCTURE_NOT_TRUSTED:
        'Untrusted infrastructure (its Kubernetes target was set by a tenant administrator): this job needs a secret graal does not hand to this cluster — job identity, file library, low-code, private image… Run it on a platform infrastructure.',
      INFRASTRUCTURE_WORKLOAD_UNSUPPORTED:
        'This infrastructure accepts no workspace, endpoint, stream or warehouse: it is untrusted or has its own namespace. Choose a platform infrastructure.',
      SECRET_STORE_UNAVAILABLE:
        'The organisation’s external store cannot be reached: the secret value can be neither read nor written. Retry later.',
      SECRET_STORE_DENIED:
        'The organisation’s external store denies access: an administrator must check its credentials (Secret store).',
      SECRET_STORE_VALUE_MISSING:
        'This secret’s value no longer exists in the external store: enter it again.',
      SECRET_STORE_NOT_CONFIGURED:
        'This secret lives in an external store that is no longer designated: an administrator must configure it again (Secret store).',
    },
    states: {
      empty: {
        title: 'Nothing here yet',
        description: 'This list is empty. Create a first item to fill it.',
      },
      'no-results': {
        title: 'No results',
        description: 'No item matches the current search or filters.',
      },
      forbidden: {
        title: 'Access denied',
        description:
          'Your permissions do not cover this resource. Ask an organisation administrator for access.',
      },
      'not-found': {
        title: 'Resource not found',
        description: 'It may have been deleted, or the identifier is wrong.',
      },
      unavailable: {
        title: 'Feature unavailable',
        description:
          'This feature is not enabled on this installation. The rest of the screen works normally.',
      },
      busy: {
        title: 'Database momentarily busy',
        description: 'The database is momentarily busy. Try again in a moment.',
        retryIn_one: 'The database is momentarily busy. Try again in {{count}} second.',
        retryIn_other: 'The database is momentarily busy. Try again in {{count}} seconds.',
      },
      error: {
        title: 'Something went wrong',
        description: 'The request did not go through. Try again in a moment.',
      },
    },
    permissions: {
      denied: 'You are not allowed to perform this action.',
      checking: 'Checking your permissions…',
    },
    list: {
      searchPlaceholder: 'Search…',
      allOf: 'All: {{name}}',
      clearFilters: 'Clear filters',
      range: '{{from}}–{{to}} of {{total}}',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      sort: {
        none: 'Sort',
        asc: 'Sorted ascending',
        desc: 'Sorted descending',
      },
    },
    detail: {
      back: 'Back',
    },
    form: {
      save: 'Save',
      create: 'Create',
      cancel: 'Cancel',
      optional: 'optional',
      importFile: 'Import a file',
      choose: 'Choose…',
      noChange: 'Nothing to save.',
      addPair: 'Add an entry',
      removePair: 'Remove entry',
      pairKey: 'Key',
      pairValue: 'Value',
      addItem: 'Add',
      removeItem: 'Remove',
      errors: {
        required: 'This field is required.',
        invalid: 'Invalid value.',
        invalidName: 'Lowercase letters, digits and dashes only.',
        invalidCron: 'Invalid cron expression.',
        tooLong: 'Value is too long.',
        positive: 'Enter a positive number.',
        fileTooLarge: 'File too large (1 MiB at most).',
        fileUnreadable: 'Unreadable file.',
      },
      cron: {
        count: 'A cron expression has 5 fields (min h day month weekday), or 6 with seconds.',
        field: 'Field “{{field}}”: {{reason}}',
        range: 'value out of range',
        syntax: 'unrecognised syntax',
        fields: {
          second: 'seconds',
          minute: 'minutes',
          hour: 'hours',
          dayOfMonth: 'day of month',
          month: 'month',
          dayOfWeek: 'day of week',
        },
        presets: {
          dailySix: 'Every day at 6am',
          hourly: 'Every hour',
          weekly: 'Every Monday at 6am',
        },
      },
    },
    wizard: {
      steps: 'Steps',
      previous: 'Back',
      next: 'Next',
      finish: 'Finish',
      position: 'Step {{current}} of {{total}}',
    },
  },
  nav: {
    label: 'Main navigation',
    collapse: 'Collapse sidebar',
    collapseShort: 'Collapse',
    expand: 'Expand sidebar',
    project: {
      caption: 'Project',
      choose: 'Choose a project',
      switch: 'Project {{name}} — switch project',
      group: 'Project {{name}}',
      none: 'No project open.',
      noneResume: 'No project open. Resume:',
      resume: 'Resume {{name}}',
      noneEmpty: 'No project open: the organization has none yet.',
    },
    organisation: {
      caption: 'Organization',
      group: 'Organization {{name}}',
    },
    groups: {
      project: 'Project',
      home: 'Home',
      build: 'Build',
      data: 'Data',
      workspaces: 'Workspaces',
      ml: 'ML',
      execution: 'Execution',
      settings: 'Settings',
      account: 'Usage & support',
      billing: 'Billing',
      ai: 'AI and agents',
    },
    items: {
      overview: 'Overview',
      activity: 'Activity',
      projects: 'All projects',
      jobs: 'Jobs',
      workflows: 'Workflows',
      triggers: 'Triggers',
      editor: 'Low-code editor',
      libraries: 'Libraries',
      buckets: 'Buckets',
      catalog: 'Catalog',
      glossary: 'Glossary',
      connections: 'Data connections',
      lineage: 'Lineage',
      sql: 'SQL warehouse',
      streams: 'Streams',
      jupyter: 'Jupyter',
      vscode: 'VS Code',
      rstudio: 'RStudio',
      sessions: 'Sessions',
      experiments: 'Experiments',
      models: 'Models',
      endpoints: 'Endpoints',
      infrastructures: 'Infrastructures',
      instanceTypes: 'Instance types',
      runtimes: 'Runtimes',
      devices: 'Devices',
      bridges: 'Bridges',
      members: 'Members',
      roles: 'Roles and assignments',
      tokens: 'Tokens & agents',
      secrets: 'Secrets',
      secretStore: 'Secret store',
      firewall: 'Firewall',
      auditTrail: 'Audit trail',
      branding: 'White label',
      mfa: 'Two-factor',
      audit: 'Sign-ins',
      usage: 'Usage & costs',
      quotas: 'Quotas & capacity',
      tickets: 'Support',
      invoices: 'Invoices',
      transactions: 'Transactions',
      paymentMethods: 'Payment methods',
      subscription: 'Subscription',
      pricing: 'Pricing',
      llmGateway: 'LLM gateway',
      llmKeys: 'Gateway keys',
      assistant: 'Document assistant',
      codeAssistant: 'Code assistant',
      agents: 'Agents',
      agentPolicies: 'Agent policies',
    },
  },
  topbar: {
    breadcrumb: 'Breadcrumb',
    search: 'Search…',
    agents: 'Agents',
    agentsConnected_one: '{{count}} agent connected',
    agentsConnected_other: '{{count}} agents connected',
    notifications: 'Notifications',
    unread_one: '{{count}} unread',
    unread_other: '{{count}} unread',
    markAllRead: 'Mark all as read',
    viewAll: 'View all',
    tenant: 'Organization',
    switchTenant: 'Switch organization',
    project: 'Project',
    switchProject: 'Switch project',
    allProjects: 'All projects',
    manageTenants: 'Manage organizations',
    userMenu: 'User menu',
    profile: 'Profile',
    following: 'Following',
    preferences: 'Preferences',
    theme: 'Theme',
    language: 'Language',
    documentation: 'Documentation',
    apiDocs: 'API documentation',
    thirdPartyNotices: 'Third-party licences',
    signOut: 'Sign out',
    dark: 'Dark',
    light: 'Light',
    system: 'System',
  },
  status: {
    queued: 'Queued',
    pending: 'Pending',
    running: 'Running',
    succeeded: 'Succeeded',
    failed: 'Failed',
    cancelled: 'Cancelled',
    retrying: 'Retrying',
    skipped: 'Skipped',
  },
  jobTypes: {
    python: 'Python',
    bash: 'Bash',
    spark: 'Spark',
    lowcode: 'Low-code',
    docker: 'Docker',
    dbt: 'dbt',
    dask: 'Dask',
    ray: 'Ray',
    flink: 'Flink',
    tensorflow: 'TensorFlow',
    pytorch: 'PyTorch',
    mxnet: 'MXNet',
    xgboost: 'Distributed XGBoost',
    notebook: 'Notebook',
    knime: 'KNIME',
    'database-migration': 'Database migration (Liquibase)',
    hive: 'Hive (HiveQL)',
    apachesqoop: 'Sqoop',
    apachehadoop: 'Hadoop',
    apachemapreduce: 'MapReduce',
    beam: 'Apache Beam',
    'spring-batch': 'Spring Batch',
    'spring-cloud-data-flow': 'Spring Cloud Data Flow',
    'akka-data-pipeline': 'Pekko pipeline (formerly Akka)',
    pulsar: 'Pulsar',
    airflow: 'Airflow',
    sql: 'SQL',
    sas: 'SAS',
    'hadoop-assess': 'Hadoop assessment',
    'hdfs-copy': 'HDFS → S3 copy',
    'iceberg-migration': 'Hive → Iceberg conversion',
    compare: 'Double run comparison',
    'kafka-mirror': 'Kafka replication (MirrorMaker 2)',
  },
  agent: {
    badge: 'Agent',
    createdBy: 'Created by an AI agent through MCP',
    via: 'via {{client}}',
  },
  projects: {
    title: 'Projects',
    subtitle: 'Projects of organisation {{tenant}}.',
    new: 'New project',
    newTitle: 'New project',
    newSubtitle: 'A project groups jobs, workflows and their data.',
    searchPlaceholder: 'Search a project…',
    columns: {
      name: 'Name',
      description: 'Description',
      badge: 'Marker',
      labels: 'Labels',
      owner: 'Owner',
      updated: 'Updated',
    },
    fields: {
      name: 'Name',
      nameHelp: 'Lowercase letters, digits and dashes — used as the identifier in URLs.',
      description: 'Description',
      badge: 'Visual marker',
      badgeHelp: 'Colour of the project in the navigation.',
      labels: 'Labels',
    },
    badges: {
      primary: 'Brand',
      info: 'Information',
      success: 'Success',
      warning: 'Warning',
      danger: 'Critical',
      neutral: 'Neutral',
    },
    empty: {
      title: 'No project yet',
      description: 'Create a first project to hold your jobs and workflows.',
    },
    created: 'Project “{{name}}” created.',
    openJobs: 'View jobs',
    emptyTenant: {
      title: 'No project in this organization',
      description:
        'Jobs, workflows and permissions live in a project. Create the first one: the overview, jobs and workflows will open in it.',
    },
  },
  workflows: {
    oozie: {
      entry: 'Import from Oozie',
      title: 'Import from Oozie',
      intro:
        'Paste the Oozie workflow (and its coordinator): spark, hive, sqoop, java and shell actions become jobs, fork, join and transitions become dependencies, the coordinator becomes the cron of the workflow. Everything else is listed with its reason. The dry run writes nothing; applying creates or updates by name.',
      coordinatorHint: 'Optional: a time coordinator becomes the cron of the workflow.',
      propertiesHint: 'The values of the ${…}, one “key=value” per line.',
      libraries: 'Jars (libraries)',
      librariesHint: 'One “name.jar=library key” line per jar of lib/, already uploaded to Libraries.',
      rewrites: 'Path rewrites',
      rewritesHint:
        'One “hdfs://nn:8020/data -> s3a://lake/hdfs/data” line per prefix (plan of the HDFS copy).',
      files: 'Scripts (HiveQL, shell)',
      filesHint: 'The files named by the hive and shell actions, at most 256 KiB each.',
      fileTooLarge: '“{{name}}” is larger than 256 KiB.',
      namePrefix: 'Name prefix',
      instanceType: 'Instance type of the jobs',
      instanceTypeHint: 'Required to apply.',
      instanceTypePlaceholder: 'Choose an instance type',
      identity: 'Identity of the jobs',
      identityHint: 'Required to apply: the jobs and the workflow run as this identity.',
      identityPlaceholder: 'Choose an identity',
      invalidLine: '{{field}}: line {{line}} has no separator.',
      dryRun: 'Dry run',
      apply: 'Apply',
      failed: 'Import refused.',
      reportDryRun: 'Dry run: nothing is written',
      reportApplied: 'Import applied',
      translated: '{{translated}} of {{actions}} action(s) translated',
      workflow: 'Workflow',
      schedule: 'cron {{cron}} ({{timezone}})',
      created: 'created',
      updated: 'updated',
      untranslated: 'Not translated',
      missing: 'Missing from libraries or scripts: {{names}}.',
      unresolved: 'Without a value in job.properties: {{names}}',
    },
    title: 'Workflows',
    subtitle: 'Job orchestration for project {{project}}.',
    subtitle_unnamed: 'Job orchestration for this project.',
    new: 'New workflow',
    newTitle: 'New workflow',
    searchPlaceholder: 'Search a workflow…',
    columns: {
      name: 'Name',
      schedule: 'Schedule',
      tasks: 'Tasks',
      updated: 'Updated',
      taskName: 'Step',
      taskType: 'Type',
      taskDepends: 'Depends on',
    },
    empty: {
      title: 'No workflow in this project',
      description: 'A workflow chains several jobs and carries their schedule.',
    },
    steps: {
      basics: 'Definition',
      basicsHelp: 'Name and description of the workflow.',
      tasks: 'Tasks',
      tasksHelp: 'What the workflow chains: project jobs, or a verification script.',
      schedule: 'Schedule',
      scheduleHelp: 'Manual, or scheduled with a cron expression validated before sending.',
      review: 'Review',
      reviewHelp: 'Check before creating: nothing is sent until you finish.',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      scheduleType: 'Trigger',
      scheduleOnce: 'Manual',
      scheduleCron: 'Scheduled (cron)',
      cronExpression: 'Cron expression',
      timezone: 'Time zone',
      labels: 'Labels',
    },
    created: 'Workflow “{{name}}” created.',
    tasks: {
      name: 'Step name',
      kind: 'Kind',
      kinds: {
        job: 'Run a job',
        script: 'Verification script',
        workflow: 'Run a sub-workflow',
        condition: 'Condition (branching)',
        time: 'Wait',
        error: 'Fail the workflow',
      },
      job: 'Job',
      after: 'After',
      noDependency: 'None (starts the workflow)',
      untitled: 'Step {{position}}',
      script: 'Script',
      scriptHelp: 'One command per line. A non-zero exit code fails the workflow.',
      add: 'Add a step',
      remove: 'Remove this step',
      atLeastOne: 'A workflow chains at least one step.',
      duplicateName: 'Two steps cannot have the same name.',
      workflow: 'Workflow',
      workflowHelp:
        'A workflow of the same project, started then awaited until it ends. You need the right to run it; a cycle is refused.',
      expression: 'Expression',
      expressionHelp:
        'Reads the status (steps.<step>.status: succeeded or skipped) or the published results (steps.<step>.results.<key>) of upstream steps; comparisons == != < <= > >=, and ! && || and parentheses. True: the next steps run. False: the step and everything depending on it are skipped.',
      expressionUpstream: 'Upstream steps: {{steps}}.',
      expressionNoUpstream: 'This step has no upstream step to cite yet.',
      waitMode: 'Wait',
      waitModes: {
        duration: 'A duration',
        until: 'Until a date',
      },
      duration: 'Duration (seconds)',
      durationHelp: 'From the start of the step, without any pod. The workflow timeout still applies.',
      until: 'Date and time',
      untilHelp: 'ISO 8601 with a time zone, for example 2026-10-20T06:00:00Z.',
      message: 'Message',
      messageHelp: 'The reason given for the failure of the workflow. An error step is never retried.',
      tooLong: '{{max}} characters at most.',
      invalidUntil: 'An ISO 8601 date with a time zone is expected (2026-10-20T06:00:00Z).',
      invalidDuration: 'A whole number of seconds, between 0 and {{max}}.',
      seconds_one: '{{count}} second',
      seconds_other: '{{count}} seconds',
    },
    manual: 'Manual',
    taskCount_one: '{{count}} task',
    taskCount_other: '{{count}} tasks',
    runNow: 'Run now',
    runStarted: 'Run {{name}} started.',
    runFailed: 'Could not start the run.',
    delete: 'Delete workflow',
    deleted: 'Workflow “{{name}}” deleted.',
    deleteFailed: 'Deletion failed.',
    detail: {
      subtitle_one: 'Project {{project}} · {{count}} step.',
      subtitle_other: 'Project {{project}} · {{count}} steps.',
      subtitle_unnamed_one: '{{count}} step.',
      subtitle_unnamed_other: '{{count}} steps.',
      tabs: {
        summary: 'Summary',
        runs: 'Runs',
        notifications: 'Notifications',
        costs: 'Costs',
        permissions: 'Permissions',
      },
      totalRuns: 'Runs',
      succeeded: 'Succeeded',
      failed: 'Failed',
      running: 'Running',
      lastRun: 'Last run',
      noRuns: 'No run yet',
      runNumber: 'Run',
      updated: 'Updated on',
      identity: 'Run identity',
      tasks: 'Steps',
      tasksHint: 'Steps, their links and their parameters are edited in the graph editor (“Edit graph”).',
      noTasks: 'No step in this workflow',
      saved: 'Workflow saved.',
      notificationsOnStart: 'On start',
      notificationsOnSuccess: 'On success',
      notificationsOnFailure: 'On failure',
      noNotifications: 'No notification configured on this workflow.',
      permissionsHint: 'Roles granted on this workflow decide who can read, run and edit it.',
      grantRoles: 'Manage roles',
    },
    run: {
      title: 'Run {{name}}',
      subtitle: 'Run of workflow {{workflow}}.',
      subtitle_unnamed: 'Workflow run.',
      tabs: {
        summary: 'Summary',
        events: 'Events',
      },
      runId: 'Run id',
      workflow: 'Workflow',
      steps: 'Steps',
      stepsHint: 'Status derived from events: the contract exposes no per-step status.',
      timeline: 'Timeline',
      timelineLabel: 'Run event timeline',
      noSteps: 'This workflow has no step to follow',
      noEvents: 'No event yet',
      noEventsHelp: 'The run was just created: events arrive as it progresses.',
      searchPlaceholder: 'Search an event…',
      wholeRun: 'whole run',
      liveLabel: 'Follow',
      liveOn: 'live (5 s)',
      liveOff: 'finished',
      kill: 'Stop',
      killed: 'Run stop requested.',
      killFailed: 'Could not stop the run.',
      delete: 'Delete run',
      deleted: 'Run deleted.',
      deleteFailed: 'Deletion failed.',
      openChildRun: 'Open run',
      columns: {
        at: 'Time',
        type: 'Event',
        step: 'Step',
        childRun: 'Child run',
      },
      types: {
        prestart: 'Preparing',
        started: 'Started',
        prestop: 'Stopping',
        complete: 'Completed',
        failed: 'Failed',
      },
    },
  },
  jobs: {
    title: 'Jobs',
    subtitle:
      'The jobs of project {{project}}: scripts, Spark, low-code, distributed computing, machine learning.',
    subtitle_unnamed:
      'The jobs of this project: scripts, Spark, low-code, distributed computing, machine learning.',
    new: 'New job',
    filterPlaceholder: 'Filter jobs…',
    fields: {
      name: 'Name',
      description: 'Description',
      labels: 'Labels',
      env: 'Environment variables',
      parameters: 'Positional parameters',
      module: 'Module',
      moduleHelp: 'Name of the Python module to run.',
      lines: 'Script',
      linesHelp: 'One command per line, run by bash.',
      mainLibrary: 'Main library',
      mainLibraryHelp: 'Jar or py file uploaded to the project libraries.',
      mainClassName: 'Main class',
      pyFiles: 'Additional Python files',
      conf: 'Spark configuration (--conf)',
      definition: 'Low-code graph',
      definitionHelp: 'Graph built in the low-code editor.',
      mode: 'Execution mode',
      instanceType: 'Instance type',
      driverInstanceType: 'Driver instance type',
      executorInstanceType: 'Executor instance type',
      numberExecutors: 'Number of executors',
      scheduleType: 'Trigger',
      scheduleOnce: 'Manual',
      scheduleCron: 'Scheduled (cron)',
      cronExpression: 'Cron expression',
      timeoutSeconds: 'Timeout (s)',
      maxRetries: 'Extra attempts',
      secrets: 'Secrets',
      infrastructureId: 'Execution infrastructure',
      infrastructureIdHelp: 'A scheduled job creates its own runs: they start on this infrastructure.',
      identityId: 'Execution identity',
      identityIdHelp:
        'Machine account the job runs as: it fetches the job files and secrets. Required as soon as the job references a secret.',
      libraries: 'Libraries',
      librariesHelp:
        'Files uploaded in Libraries, copied into the run working directory (/workspace): code, jars, data, SAS programs called by %INCLUDE.',
      notebook: 'Notebook (path)',
      notebookHelp: 'Path of a .ipynb in the job files. Or paste the notebook below — one of the two.',
      notebookSource: 'Notebook (content)',
      notebookSourceHelp: 'The .ipynb itself (nbformat 4 JSON, at most 512 KiB).',
      notebookParameters: 'Parameters',
      notebookParametersHelp: 'A JSON object, injected after the cell tagged “parameters” (like papermill).',
      kernel: 'Kernel',
      cellTimeoutSeconds: 'Timeout per cell (s)',
      experiment: 'MLflow experiment',
      hiveQuery: 'HiveQL query',
      hiveQueryHelp: 'Run by spark-sql on the Iceberg catalog; one statement per line.',
      sasProgram: 'SAS program',
      sasProgramHelp:
        'Run by graal-sas, without a SAS licence: DATA step, common PROCs, macros, LIBNAME to Iceberg, S3 or PostgreSQL. Optional with a main program: it then runs BEFORE it (%LET, OPTIONS, LIBNAME). Single files (.xpt, .sas7bdat, CSV) are picked from Libraries in the Advanced step; a relative path refers to them. A construct outside the published list fails the run with a named ERROR (exit code 3), never silently.',
      sasMainProgram: 'Main program',
      sasMainProgramHelp:
        'Path of the program, relative, ending in .sas: inside the project (archive or repository), which becomes the working directory, or, without a project, among the Libraries files copied into /workspace. %INCLUDE, LIBNAME and autocall resolve there.',
      sasArchive: 'Project archive',
      sasArchiveHelp: 'A .zip uploaded to Libraries, extracted in the run. Excludes the Git repository.',
      sasRepositoryUrl: 'Project Git repository',
      sasRepositoryUrlHelp: 'https URL only, cloned when the run starts. Excludes the archive.',
      sasRepositoryRevision: 'Revision (branch, tag or commit)',
      sasRepositoryPath: 'Project subfolder',
      sasRepositoryUsername: 'Git user',
      sasRepositoryPassword: 'Git password or token',
      sasRepositoryPasswordHelp:
        'Sealed by graal when saved and never shown again; it reaches the run through its Secret. A job copy does not carry it over: enter it again.',
      sasOutput: 'Output publication',
      sasOutputHelp:
        's3://<project bucket>/reports: listing, ODS reports and exports are copied there under <job name>/<run id>. The bucket belongs to the project, and the job identity reads and writes it.',
      none: 'None',
      command: 'Command',
      dbtCommandHelp: 'Full dbt command, run inside the dbt project.',
      sqoopCommandHelp:
        'The Sqoop line without the word “sqoop”: import, export, eval, list-tables or list-databases. No password on the line. With a project connection, neither --connect nor --username (e.g. import --table conso --hive-import --hive-table demo.conso); without one, --connect jdbc:… is required.',
      sqoopCommandPlaceholder: 'import --table conso --target-dir s3a://<bucket>/sqoop/conso',
      sqoopConnection: 'Project connection',
      sqoopConnectionHelp:
        'A database of the project (PostgreSQL, MySQL, MariaDB, SQL Server or Oracle). Once chosen, it provides --connect, --username and the password.',
      sqoopNoConnection: 'None — --connect jdbc:… on the line',
      adapter: 'Adapter',
      target: 'Target',
      profile: 'Profiles directory',
      profileHelp: 'Empty: the profile provided by graal.',
      datawarehouseId: 'Target warehouse',
      datawarehouseIdHelp: 'Identifier of the warehouse whose schema is migrated.',
      repositoryUrl: 'Changelog Git repository',
      repositoryPath: 'Changelog path',
      repositoryRevision: 'Revision (branch, tag or commit)',
      workflow: 'KNIME workflow',
      knimeWorkflowHelp: 'Name of the workflow to run inside the archive.',
      archive: 'Workflow archive',
      archiveHelp: '.knwf file uploaded to the project libraries.',
      preferences: 'KNIME preferences',
      dagId: 'DAG identifier',
      packageName: 'Python package',
      moduleName: 'Module',
      functionName: 'Function',
      numberWorkers: 'Number of workers',
      numberWorkersMax: 'Maximum number of workers',
      workerInstanceType: 'Worker instance type',
      jobManagerInstanceType: 'JobManager instance type',
      taskManagerInstanceType: 'TaskManager instance type',
      sparkExecutors: 'Spark executors',
      numberTaskManagers: 'Number of TaskManagers',
      runner: 'Beam runner',
      runnerHelp: 'direct: in the pod; flink: a standalone Flink cluster created for the run.',
      brokerInstanceType: 'Broker instance type',
      numberBrokers: 'Number of brokers',
      numberReplicas: 'Number of replicas',
      replicaInstanceType: 'Replica instance type',
      masterInstanceType: 'Master instance type',
      schedulerInstanceType: 'Scheduler instance type',
      serverInstanceType: 'Server instance type',
      image: 'Image',
      imageHelp: 'Project image, from a registry allowed by the installation. It runs under an enforced UID.',
      memberInstanceTypeHelp: 'Size of EACH member (workers, master, scheduler): one pod template.',
      gpu: 'GPU',
      gpuHelp: 'Needs an instance type with GPUs and an infrastructure that declares its GPU nodes.',
      statement: 'SQL query',
      statementHelp: 'A single read statement (SELECT, WITH, VALUES).',
      sqlDatawarehouseIdHelp: 'Identifier of the queried warehouse; you must be able to read it.',
      output: 'Output',
      table: 'Target table',
      sqlTableHelp: 'Output “table”: an Iceberg table of the warehouse schema (lower case, digits, _).',
      sqlMode: 'If the table exists',
      format: 'File format',
      path: 'Data lake folder',
      sqlPathHelp: 'Output “file”: relative folder of the data lake. Empty: sql-results/<job>/<run>.',
      port: 'Exposed port',
      properties: 'JVM properties (-D)',
      sparkExecutorsHelp: '0 or 1: local, in a single pod. From 2: a Spark cluster for the run (16 at most).',
      numberTaskManagersHelp:
        '0: Flink in local mode, in the run pod; above, a standalone cluster created for the run.',
      topics: 'Pulsar topics',
      topicsHelp:
        'Created before the program; short name (public/default) or persistent://tenant/namespace/topic.',
      passwordEnv: 'JDBC password secret',
      passwordEnvHelp:
        'Name of a job secret: it never appears on the command line. Without a project connection only.',
      taskName: 'Task name (spring.cloud.task.name)',
      taskNameHelp: 'Empty: the job name.',
      flinkConf: 'Flink configuration (-D)',
      resourceManagerConnectionId: 'ResourceManager (YARN)',
      jobHistoryConnectionId: 'JobHistory (MapReduce)',
      oozieConnectionId: 'Oozie',
      rangerConnectionId: 'Ranger',
      hdfsConnectionId: 'HDFS (WebHDFS)',
      assessNoSource: 'Not read',
      assessHttpSourceHelp:
        'A “REST API” connection of the project, without authentication; a Kerberized cluster uses the ticket of the HDFS connection (SPNEGO).',
      assessHdfsSourceHelp:
        'An HDFS connection of the project; when Kerberized, it also gives the ticket of the other sources.',
      hdfsPaths: 'Measured HDFS directories',
      hdfsPathsHelp: 'Absolute paths, at most 20; empty: the root. Each direct subdirectory is measured.',
      windowDays: 'Window (days)',
      windowDaysHelp: 'YARN applications of the last N days (at most 365).',
      maxApplications: 'Maximum applications read',
      maxApplicationsHelp: 'Per source (at most 10,000); beyond that, the report says it is truncated.',
      copyHdfsConnectionHelp:
        'The HDFS connection of the project (namenode RPC); when Kerberized, it gives the ticket of the run.',
      copySources: 'Copied HDFS directories',
      copySourcesHelp: 'Absolute paths, at most 50; each goes under <target>/<its name>.',
      copyTarget: 'S3 target',
      copyTargetHelp:
        's3a://<bucket of the project>[/<prefix>]: the identity of the job must read and write it.',
      copyOverwrite: 'Rewrite everything',
      copyOverwriteHelp: 'Otherwise only what is missing or differs is copied (-update).',
      copyVerify: 'Verify after copy',
      copyVerifyHelp: 'Compares files and bytes on both sides; a difference fails the run.',
      metastoreConnectionId: 'Hive metastore',
      migrationMetastoreHelp: 'The “Hive metastore” connection of the project where the tables live.',
      migrationHdfsHelp:
        'The HDFS connection where their files live; when Kerberized, it gives the ticket of the run.',
      migrationTables: 'Hive tables',
      migrationTablesHelp: 'database.table, at most 100.',
      migrationAction: 'Action',
      migrationActionHelp:
        'copy rewrites the data into the graal warehouse; snapshot and add_files reference the files in place; migrate turns the table into Iceberg IN the metastore of the client.',
      migrationNamespace: 'Target Iceberg namespace',
      migrationAllowMigrate: 'Allow migrate',
      migrationAllowMigrateHelp: 'migrate writes into the metastore of the client: tick it explicitly.',
      compareLeft: 'Left side (old)',
      compareRight: 'Right side (new)',
      compareSideHelp:
        'graal:database.table (graal Iceberg), hive:database.table (client metastore) or parquet:|orc:s3a://bucket/prefix.',
      compareKeys: 'Key columns',
      compareKeysHelp:
        'With keys: rows missing on one side and different rows; without: a row by row multiset comparison.',
      compareMetastoreHelp: 'Required for a hive: side — the “Hive metastore” connection of the project.',
      mirrorSourceStreamId: 'Source stream',
      mirrorTargetStreamId: 'Target stream',
      mirrorStreamHelp:
        'The id of a Kafka stream of the tenant: external (external-kafka options) or managed and running.',
      mirrorTopics: 'Replicated topics',
      mirrorTopicsHelp: 'Names or simple regular expressions (sales\\..*), at most 50; names are kept.',
      mirrorSourcePasswordEnv: 'SASL password secret (source)',
      mirrorTargetPasswordEnv: 'SASL password secret (target)',
      mirrorPasswordEnvHelp:
        'The name of a secret OF THE JOB (Secrets tab) holding the password; required for a SASL stream.',
      compareFailOnDifference: 'Fail on a difference',
      compareFailOnDifferenceHelp:
        'Otherwise the run succeeds and the verdict (equal) is read in its results, for instance in a workflow condition.',
      dockerImage: 'Container image',
      dockerImageHelp:
        'Replaces the runtime image of the type (docker_image), from a registry allowed by the installation. Empty: the platform image.',
      projectDir: 'dbt project folder in the image',
      projectDirHelp:
        'Absolute path of the dbt project shipped in the container image. Empty: the project comes from the job libraries.',
      secretKept: 'A sealed value is stored: it is never shown again. Left empty, it is kept.',
    },
    summary: {
      title: '{{type}} configuration',
      notSet: '—',
      yes: 'Yes',
      no: 'No',
      otherHint: 'Other contract options, shown as stored (secrets are masked):',
      uneditedHint: 'Options kept as they are, with no field here: {{keys}}.',
      dag_one: 'DAG frozen at creation: {{count}} task',
      dag_other: 'DAG frozen at creation: {{count}} tasks',
    },
    typeHelp: {
      python: 'A Python module, run in a pod.',
      bash: 'A shell script, one command per line.',
      spark: 'Spark in local[*], or a master and workers created for the run.',
      lowcode: 'Code generated from an editor graph, in Pandas or PySpark.',
      docker: 'A project image, from an allowed registry.',
      dbt: 'A dbt project run against Trino.',
      dask: 'A Dask scheduler and its workers, created for the run.',
      ray: 'A Ray head and its workers, created for the run.',
      flink: 'A standalone Flink application cluster, for the duration of the run.',
      tensorflow: 'Distributed training: N pods with rendezvous, optional GPU.',
      pytorch: 'Distributed training (torchrun): N pods with rendezvous, optional GPU.',
      mxnet: 'Distributed MXNet training: scheduler, servers and workers.',
      xgboost: 'Distributed XGBoost: a master and workers.',
      notebook: 'A Jupyter notebook run end to end; the executed notebook is kept in MLflow.',
      knime: 'A KNIME workflow in batch mode.',
      'database-migration': 'A Liquibase changelog applied to a warehouse.',
      hive: 'HiveQL run by spark-sql on the Iceberg catalog.',
      apachesqoop: 'A Sqoop import or export, between JDBC and S3 or Iceberg.',
      apachehadoop: 'A local “hadoop jar”, or through the YARN emulator.',
      apachemapreduce: 'A MapReduce job, local or through the YARN emulator.',
      beam: 'A Beam pipeline: DirectRunner in the pod, or FlinkRunner on a cluster created for the run.',
      'spring-batch': 'A Spring Batch application in a JVM pod.',
      'spring-cloud-data-flow': 'A Spring Cloud Data Flow task in a JVM pod.',
      'akka-data-pipeline': 'An Apache Pekko pipeline (Akka’s successor) in a JVM pod.',
      pulsar: 'A standalone Pulsar, managed by graal.',
      airflow: 'An Airflow DAG run in a pod.',
      sql: 'A read query on a data warehouse, result as a data lake file or an Iceberg table.',
      sas: 'A SAS program or project, run without a SAS licence by graal-sas.',
      'hadoop-assess':
        'Reads the client’s Hadoop estate (YARN, Oozie, HDFS, Ranger) without writing to it, and classifies its workloads for the migration.',
      'hdfs-copy':
        'Copies HDFS directories of the client to a bucket of the project (distcp), then checks files and bytes on both sides.',
      'iceberg-migration':
        'Converts Hive tables of the client into Iceberg tables of graal (copy, snapshot, add files or migrate).',
      'kafka-mirror':
        'Replicates topics from a Kafka stream (external or managed) to another with MirrorMaker 2, during a bounded window.',
      compare:
        'Compares two datasets during a double run (counts, sums, diverging rows) and stores the verdict in the results of the run.',
    },
    wizard: {
      title: 'New job',
      subtitle: 'Project {{project}}. Nothing is created until you finish.',
      subtitle_unnamed: 'Nothing is created until you finish.',
      types: 'Job types',
      soon: 'soon',
      soonHint:
        '“{{type}}” is not proven end to end yet: the API will answer “feature unavailable” (501) when it is created.',
      groups: {
        essentials: 'Essentials',
        data: 'Data',
        distributed: 'Distributed computing',
        ml: 'Machine learning',
        jvm: 'JVM and containers',
      },
      steps: {
        type: 'Type',
        typeHelp: 'What the job runs. A type marked “soon” can be selected, but is not proven yet.',
        basics: 'Definition',
        basicsHelp: 'Name, description, labels, environment variables and parameters.',
        options: 'Options',
        optionsHelp: 'The fields specific to the chosen type, and its resources.',
        schedule: 'Schedule',
        scheduleHelp: 'Manual, or scheduled by a cron expression validated before sending.',
        advanced: 'Advanced',
        advancedHelp: 'Timeout, retries, identity, secrets and libraries.',
        review: 'Review',
        reviewHelp: 'Check before creating: nothing is sent until you finish.',
      },
      create: 'Create the job',
      created: 'Job “{{name}}” created.',
      prefilledFromGraph: 'Prefilled from the low-code editor graph “{{graph}}”.',
      duplicateOf: 'Copy of job “{{name}}”: pick a new name.',
      reenterGitPassword:
        'The Git repository password is not copied (it is sealed for the original job): enter it again in the Options step.',
      dagReady_one: 'DAG ready for the low-code engine: {{count}} task.',
      dagReady_other: 'DAG ready for the low-code engine: {{count}} tasks.',
      dagFrozen: 'The saved graph’s DAG is copied into the job when it is created.',
      dagInvalid: 'Unusable graph: node “{{node}}” — {{reason}}.',
      dagIncomplete: 'Incomplete graph: {{problem}}.',
      graphUnknown: 'Graph not found in this tenant.',
      sqoop: {
        connectionNotice:
          '--connect, --username and the password come from the “{{name}}” connection: graal builds the JDBC URL and hands the password to the run through its secret, never in clear text. The line does not carry them, and the password secret stays empty.',
        lineHasConnection: 'With a project connection, remove --connect and --username from the line.',
        passwordEnvWithConnection:
          'With a project connection, leave this field empty: the password comes from the connection.',
        missingConnect: 'Choose a project connection, or write --connect jdbc:… on the line.',
      },
      openEditor: 'Open the low-code editor',
      openGraph: 'Open the graph',
      alreadyExists: 'A job with this name already exists in this project.',
      review: {
        type: 'Type',
        schedule: 'Schedule',
        notSet: '—',
      },
    },
    detail: {
      subtitle: '{{type}} job in project {{project}}.',
      tabs: {
        summary: 'Summary',
        definition: 'Definition',
        runs: 'Runs',
        costs: 'Costs',
        notifications: 'Notifications',
        permissions: 'Permissions',
        preview: 'Preview',
      },
      lastRun: 'Last run',
      totalRuns: 'Runs',
      succeeded: 'Succeeded',
      failed: 'Failed',
      running: 'Running',
      created: 'Created',
      updated: 'Updated',
      identity: 'Run identity',
      noRuns: 'This job has never run.',
      noNotifications: 'No notification configured for this job.',
      notificationsOnStart: 'On start',
      notificationsOnSuccess: 'On success',
      notificationsOnFailure: 'On failure',
      grantRoles: 'Manage roles',
      permissionsHint: 'Roles attached to this job decide who can read, run and edit it.',
      saved: 'Job saved.',
      delete: 'Delete job',
      runNumber: 'Run',
      notFound: 'Job not found.',
    },
    columns: {
      name: 'Name',
      type: 'Type',
      status: 'Status',
      lastRun: 'Last run',
      duration: 'Duration',
      schedule: 'Schedule',
      owner: 'Owner',
    },
    views: {
      all: 'All',
      running: 'Running',
      failed: 'Failing',
      scheduled: 'Scheduled',
      agents: 'Created by an agent',
    },
    pagination: '{{from}}–{{to}} of {{total}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    empty: 'No job matches the filters.',
    rowActions: 'Job actions',
    runWithInstanceType: 'Run on…',
    runNow: 'Run now',
    edit: 'Edit',
    duplicate: 'Duplicate',
    viewRuns: 'View runs',
    runStarted: 'Run {{name}} started.',
    runFailed: 'Could not start the run.',
  },
  run: {
    outputs: {
      title: 'Produced files',
      description: 'What the run published (listing, reports, exports), as declared in its summary.',
      columns: { name: 'Name', kind: 'Type', size: 'Size' },
      download: 'Download {{name}}',
      browse: 'Open the folder of {{name}} in the bucket',
      downloadFailed: 'Download failed',
    },
    title: 'Run #{{number}}',
    tabs: {
      logs: 'Logs',
      events: 'Events',
      metrics: 'Metrics',
      files: 'Files',
      parameters: 'Parameters',
      quality: 'Quality',
      lineage: 'Lineage',
      report: 'Report',
    },
    report: {
      title: 'Execution report',
      producer: 'Produced by {{producer}}',
      invocations: 'Commands: {{list}}',
      invocation: '{{command}} (exit {{code}})',
      truncated: 'Truncated report: steps were removed to stay within the limit.',
      search: 'Search steps…',
      noSteps: 'No step declared.',
      empty: {
        title: 'No report for this run',
        description:
          'A run declares its report by printing a GRAAL_RUN_REPORT line in its log. The dbt runtime does it on its own; a custom image can do the same.',
      },
      columns: {
        outcome: 'Outcome',
        step: 'Step',
        kind: 'Kind',
        invocation: 'Command',
        duration: 'Duration',
        rows: 'Rows',
        failures: 'Failing',
        message: 'Message',
      },
      outcomes: {
        success: 'Succeeded',
        failure: 'Failed',
        warning: 'Warning',
        skipped: 'Skipped',
        unknown: 'Unknown',
      },
    },
    started: 'Started {{time}}',
    elapsed: 'Duration',
    triggeredBy: 'Triggered by',
    image: 'Image',
    resources: 'Resources',
    attempt: 'Attempt',
    details: 'Details',
    runId: 'Run ID',
    job: 'Job',
    project: 'Project',
    instanceType: 'Instance type',
    schedule: 'Schedule',
    cancelRun: 'Cancel run',
    cancelled: 'Cancellation requested: the run is stopping.',
    cancelFailed: 'Could not cancel the run.',
    rerun: 'Rerun',
    notFound: 'Run not found.',
    infrastructure: 'Infrastructure',
    initiator: 'Started by',
    copyAs: 'Copy as…',
    copyAsCurl: 'curl command',
    copyAsMcp: 'MCP tool call',
    events: {
      columns: { at: 'Time', type: 'Event', message: 'Message' },
      search: 'Search events…',
      empty: 'No event for this run.',
      emptyHelp: 'The scheduler publishes every step of the run here: preparation, start, end.',
    },
    metrics: {
      names: {
        cpu: 'CPU (cores)',
        memory: 'Memory (MiB)',
        fsReads: 'Disk reads',
        fsWrites: 'Disk writes',
        netRx: 'Network in',
        netTx: 'Network out',
      },
      min: 'min. {{value}}',
      max: 'max. {{value}}',
      noPoints: 'No measurement over the run’s duration.',
      summary: '{{metric}}: last value {{last}}, minimum {{min}}, maximum {{max}}.',
    },
    files: {
      description: 'Files attached to this run.',
      columns: { name: 'File' },
      search: 'Search files…',
      download: 'Download {{name}}',
      downloadFailed: 'Could not download the file.',
      empty: 'No file for this run.',
      emptyHelp: 'A run that writes its results makes them visible here.',
    },
    parameters: {
      title: 'Run parameters',
      description: 'Positional arguments passed to this execution.',
      columns: { index: 'Position', value: 'Value' },
      empty: 'This run received no parameter.',
      input: 'Input (JSON)',
    },
    copiedAs: '{{format}} copied to clipboard',
    launch: {
      instanceType: 'Run instance type',
      instanceTypeHelp:
        'Optional: replaces, for this run only, the instance type of the job and its members.',
      jobInstanceType: 'The job’s',
      title: 'Choose the execution infrastructure',
      description:
        'This organization has several infrastructures. The run will execute on the one you choose.',
      field: 'Infrastructure',
      submit: 'Run',
      none: 'No execution infrastructure',
      noneHelp:
        'A run executes on an infrastructure, and this organization exposes none. Ask an administrator to declare one.',
      loadFailed: 'The organization’s infrastructures could not be read: the run was not started.',
    },
  },
  logs: {
    search: 'Search logs',
    matches: '{{current}} / {{total}}',
    noMatch: 'No match',
    previousMatch: 'Previous match',
    nextMatch: 'Next match',
    follow: 'Follow',
    wrap: 'Wrap lines',
    download: 'Download logs',
    downloadFailed: 'Could not download the log: {{detail}}',
    lines_one: '{{count, number}} line',
    lines_other: '{{count, number}} lines',
    newLines_one: '{{count}} new line',
    newLines_other: '{{count}} new lines',
    live: 'Live',
    region: 'Run log',
    source: 'Source',
    allSources_one: 'All sources ({{count}})',
    allSources_other: 'All sources ({{count}})',
    mode: {
      polling: 'Log re-read every 5 s (the live stream is not available)',
      ended: 'Run finished: full log',
    },
    dropped_one: '{{count, number}} old line removed from the screen — the download keeps everything',
    dropped_other: '{{count, number}} old lines removed from the screen — the download keeps everything',
  },
  apiDocs: {
    title: 'API documentation',
    subtitle:
      'The graal REST contract, served by this installation. “Try it out” acts with your rights, in this organisation.',
    frameTitle: 'Interactive API documentation',
    unavailable: 'The interactive documentation is served by graal-api: it does not exist in mock mode.',
  },
  palette: {
    title: 'Command palette',
    placeholder: 'Search a resource or run a command…',
    empty: 'No results.',
    groups: {
      suggestions: 'Suggestions',
      project: 'Project',
      projectOf: 'Project · {{name}}',
      organisation: 'Organization',
      organisationOf: 'Organization · {{name}}',
      recent: 'Recent',
      projects: 'Projects',
      agents: 'AI agents',
      appearance: 'Appearance',
      help: 'Help',
    },
    apiDocs: 'API documentation',
    newJob: 'Create a job',
    newProject: 'Create a project',
    newWorkflow: 'Create a workflow in this project',
    runJob: 'Run “{{name}}”',
    openEditor: 'Open the low-code editor',
    connectAgent: 'Connect an agent (MCP)',
    agentActivity: 'View agent activity',
    searchEverywhere: 'Search “{{query}}” everywhere',
    modes: {
      dark: 'Dark theme',
      light: 'Light theme',
      system: 'System theme',
    },
    current: '(current)',
    navigate: 'navigate',
    select: 'open',
    dismiss: 'close',
  },
  agents: {
    title: 'Agents',
    subtitle: 'AI agents connected to this tenant through MCP',
    connectedSection: 'Connected agents',
    feedSection: 'Live activity',
    connectSection: 'Connect an agent',
    live: 'Live',
    lastSeen: 'active {{time}}',
    scopes: 'Scopes',
    scopeRead: 'read',
    scopeWrite: 'write',
    revoke: 'Revoke',
    token: 'Token',
    tools: {
      create_job: 'created job',
      run_job: 'started',
      get_run_logs: 'read the logs of',
      schedule_job: 'scheduled',
      list_jobs: 'listed the jobs of',
      get_costs: 'checked the costs of',
      upload_code: 'uploaded code to',
    },
    connect: {
      claudeCode: 'Claude Code',
      anyClient: 'Other MCP client',
      claudeAi: 'claude.ai',
      claudeAiHint:
        'claude.ai › Settings › Connectors › Add custom connector: paste this address, no client ID. If claude.ai asks for one, enter graal-ui (public client, no secret). Claude Code keeps using the agent token.',
      hint: 'Create an agent token in Settings › Tokens & agents, then export it as GRAAL_AGENT_TOKEN (never commit it).',
      copy: 'Copy configuration',
      copied: 'Configuration copied',
      permissions: 'Every agent action is audited and shown here live.',
    },
  },
  editor: {
    title: 'Low-code editor',
    pipeline: 'Pipeline',
    saved: 'Saved',
    run: 'Run',
    exportCode: 'Export code',
    exportPyspark: 'PySpark',
    exportPandas: 'Pandas',
    createJob: 'Create a job',
    createJobUnsaved: 'Save the graph: the job copies the saved version.',
    createJobNoProject: 'No project in this organisation: create one to hold the job.',
    library: 'Library',
    properties: 'Properties',
    canvas: 'Pipeline canvas',
    kinds: {
      source: 'Source',
      transform: 'Transform',
      sink: 'Destination',
    },
    nodes: {
      readS3: 'Read S3',
      readSql: 'Read SQL',
      filter: 'Filter',
      select: 'Select',
      join: 'Join',
      aggregate: 'Aggregate',
      writeParquet: 'Write Parquet',
      writeTable: 'Write a SQL table',
      sort: 'Sort',
      distinct: 'Remove duplicates',
      rename: 'Rename a column',
      drop: 'Drop columns',
      union: 'Union (concatenate)',
      limit: 'Limit',
      sample: 'Sample',
      compute: 'Computed column',
      cast: 'Convert type',
      fillNull: 'Replace nulls',
      dropNull: 'Drop nulls',
      pivot: 'Pivot',
      unpivot: 'Unpivot',
      window: 'Window (rank, running total, offset, rolling)',
      explode: 'Explode a list',
      string: 'Transform text',
      datePart: 'Extract from date',
      deduplicate: 'Deduplicate by key',
      regex: 'Regular expression',
      dateParse: 'Parse a date',
      bin: 'Bin into ranges',
      readSftp: 'Read SFTP',
      writeSftp: 'Write SFTP',
      readHdfs: 'Read HDFS',
      writeHdfs: 'Write HDFS',
      readHttp: 'Read a REST API',
      readSas: 'Read SAS (sas7bdat)',
      readJson: 'Read JSON',
      writeJson: 'Write JSON',
      readIceberg: 'Read Iceberg',
      writeIceberg: 'Write Iceberg',
      readCdc: 'PostgreSQL CDC',
      expect: 'Quality checks',
    },
    fields: {
      path: 'Path',
      format: 'Format',
      condition: 'Condition',
      groupBy: 'Group by',
      aggregations: 'Aggregations',
      mode: 'Write mode',
      partitionBy: 'Partition by',
      structure: 'Structure',
      structureHelp:
        'Source columns, “column:type” separated by commas. Types: str, int32, int64, double, bool (dates are read as str). Required upstream of a join or an aggregation.',
      columns: 'Columns',
      on: 'Join key',
      how: 'Join type',
      columnsHelp: 'Comma-separated columns; empty = all columns.',
      sortBy: 'Sort by',
      sortByHelp: 'Comma-separated columns, each followed by asc (default) or desc.',
      oldName: 'Column to rename',
      newName: 'New name',
      distinct: 'Remove duplicates',
      n: 'Number of rows',
      fraction: 'Share of rows',
      fractionHelp: 'Between 0 (excluded) and 1: 0.1 keeps about one row in ten.',
      seed: 'Seed (optional)',
      output: 'Output column',
      outputHelp: 'Empty: the transformed column is replaced.',
      expression: 'Expression',
      expressionHelp: 'Numeric columns, numbers, + - * / and parentheses. A division gives a decimal.',
      casts: 'Conversions',
      castsHelp: 'Comma-separated “column:type”. Types: str, int32, int64, double, bool.',
      values: 'Replacement values',
      valuesHelp: 'Comma-separated “column = value”; text between apostrophes.',
      index: 'Columns kept as rows',
      pivotColumn: 'Column to pivot (text)',
      valueColumn: 'Value column',
      aggregation: 'Aggregation',
      pivotValues: 'Values turned into columns',
      pivotValuesHelp: 'Values of the pivoted column, comma-separated: each becomes a column.',
      idColumns: 'Columns kept',
      valueColumns: 'Unpivoted columns',
      variableColumn: 'Name column',
      function: 'Function',
      orderBy: 'Order within the window',
      orderByHelp: 'Comma-separated columns, each followed by asc (default) or desc.',
      column: 'Column',
      columnHelp: 'Column read: accumulated, shifted or averaged (numeric, except for a shift).',
      separator: 'Separator',
      separatorHelp: 'Taken literally: “|” or “.” are not regular expressions.',
      operation: 'Operation',
      part: 'Date part',
      name: 'Name',
      nameHelp: 'Lower-case letters, digits and dashes. Used as the exported file name.',
      description: 'Description',
      pathHelp: 'File path on the server, without “..” or scheme.',
      dbms: 'DBMS',
      host: 'Host',
      port: 'Port',
      portHelp: 'Empty: the DBMS default port (5432, 3306, 1433, 1521).',
      database: 'Database',
      databaseHelp: 'For Oracle: the service name.',
      schema: 'Schema',
      schemaHelp: 'Empty: the session default schema.',
      table: 'Table',
      cdcTableHelp: '“schema.table”, or “table” (public schema). One table per task.',
      user: 'User',
      userHelp: 'Optional: the HDFS user (simple authentication).',
      password: 'Password',
      passwordHelp: 'A ${secret:<id>} reference to a platform secret, never the value.',
      privateKey: 'Private key',
      privateKeyHelp: 'Or a PEM private key, as a ${secret:<id>} reference: password OR key, not both.',
      token: 'Token',
      tokenHelp: 'A ${secret:<id>} reference to the token, never the value.',
      sslMode: 'TLS encryption',
      hostKey: 'Host key fingerprint',
      hostKeyHelp:
        'Required: checked BEFORE any authentication. OpenSSH format (ssh-keygen -lf): SHA256: followed by 43 characters.',
      encoding: 'Encoding',
      lines: 'One line per record',
      namenode: 'Namenode',
      httpPort: 'WebHDFS port (Pandas)',
      rpcPort: 'RPC port (PySpark)',
      kerberos: 'Kerberos',
      kerberosHelp:
        'Yes: the run authenticates with the ticket of the Kerberized connection it cites (ADR 0105), never with a user name.',
      servicePrincipal: 'Namenode principal',
      servicePrincipalHelp: 'Empty: the Hadoop default (nn/_HOST@REALM).',
      baseUrl: 'API address',
      baseUrlHelp: 'https://host or https://host:port, without path or credentials.',
      query: 'Query parameters',
      queryHelp: '“name=value” joined with &, taken literally.',
      recordsPath: 'Records path',
      recordsPathHelp: 'Dotted path to the list in each response; empty: the whole response.',
      pagination: 'Pagination',
      pageParam: 'Page parameter',
      pageStart: 'First page',
      pageStartHelp: 'Empty: 1.',
      sizeParam: 'Size parameter',
      pageSize: 'Page size',
      cursorPath: 'Cursor path',
      cursorPathHelp: 'Dotted path, in the response, to the next page cursor.',
      maxPages: 'Maximum pages',
      auth: 'Authentication',
      header: 'Header',
      headerHelp: 'Name of the header carrying the token (letters, digits, dashes).',
      namespace: 'Namespace',
      namespaceHelp: 'Empty: the tenant one (t_<tenant>), read when the run starts.',
      keys: 'Merge keys',
      keysHelp: 'Comma-separated columns: a row with the same key is replaced; _op = d deletes it.',
      slot: 'Replication slot',
      publication: 'Publication',
      create: 'Create slot and publication if missing',
      checks: 'Checks',
      offset: 'Offset (rows)',
      offsetHelp: 'Rows before (previous value) or after (next value) the current row, within its partition.',
      size: 'Window size (rows)',
      sizeHelp:
        'The current row and the previous ones in its partition; at the start of a partition the window is partial.',
      pattern: 'Pattern',
      patternHelp:
        'Regular expression searched in the text, case-sensitive. Syntax shared by every engine: classes [A-Z], \\d, quantifiers, groups ( ) and (?: ). Rejected: \\w, \\s, \\b, (?=…), backreferences.',
      group: 'Group',
      groupHelp: '0: the whole match; 1, 2…: the group in parentheses. No match: null.',
      replacement: 'Replace with',
      replacementHelp: 'Literal text, without “\\” or “$”; empty: matches are removed.',
      dateFormat: 'Date format',
      dateFormatHelp:
        'Day, month and hours on two digits. The result is an ISO date (YYYY-MM-DD); an impossible or malformed date gives a null.',
      edges: 'Bounds',
      edgesHelp:
        'Increasing numbers separated by commas: a range goes from one bound (included) to the next (excluded).',
      labels: 'Labels',
      labelsHelp: 'One per range, separated by commas; outside the bounds or null: null.',
      deduplicateKeys: 'Key',
      deduplicateKeysHelp: 'Columns separated by commas: a single row is kept per key value.',
      deduplicateOrder: 'Row kept',
      deduplicateOrderHelp:
        'The first one in this order (column asc or desc, separated by commas): “date desc” keeps the latest. Nulls last.',
      checksHelp:
        "One check per line: not_null(col), unique(a, b), between(col, min=0, max=100), regex(col, 'pattern'), row_count(min=1), freshness(col, seconds), in_set(col, 'a', 'b'). Add severity=warning not to fail the run.",
    },
    choices: {
      distinct: { false: 'No (UNION ALL)', true: 'Yes (UNION)' },
      how: { any: 'If any column is null', all: 'If all columns are null' },
      aggregation: { sum: 'Sum', mean: 'Mean', min: 'Minimum', max: 'Maximum', count: 'Count' },
      function: {
        row_number: 'Row number',
        rank: 'Rank (with ties)',
        dense_rank: 'Dense rank',
        cumulative_sum: 'Running total',
        lag: 'Previous value',
        lead: 'Next value',
        rolling_sum: 'Rolling sum',
        rolling_mean: 'Rolling mean',
      },
      operation: {
        upper: 'Upper case',
        lower: 'Lower case',
        trim: 'Trim spaces',
        extract: 'Extract',
        replace: 'Replace',
      },
      part: { year: 'Year', quarter: 'Quarter', month: 'Month', day: 'Day' },
      dbms: {
        postgresql: 'PostgreSQL',
        mysql: 'MySQL',
        mariadb: 'MariaDB',
        sqlserver: 'SQL Server',
        oracle: 'Oracle',
      },
      sslMode: {
        default: 'Driver default',
        disable: 'Disabled',
        require: 'Required (certificate not verified)',
        verify: 'Required and verified',
      },
      mode: {
        overwrite: 'Replace',
        append: 'Append',
        merge: 'Merge (on the keys)',
      },
      format: { csv: 'CSV', parquet: 'Parquet', json: 'JSON', sas7bdat: 'SAS (sas7bdat)' },
      encoding: { infer: 'Detect', 'utf-8': 'UTF-8', 'latin-1': 'Latin-1', cp1252: 'Windows-1252' },
      lines: { false: 'No (JSON array)', true: 'Yes (JSON Lines)' },
      kerberos: { false: 'No (simple authentication)', true: 'Yes' },
      pagination: {
        none: 'None',
        page: 'By page number',
        offset: 'By offset',
        cursor: 'By cursor',
        link: 'Link header (rel="next")',
      },
      auth: {
        none: 'None',
        bearer: 'Bearer token',
        basic: 'Basic (user, password)',
        header: 'Token in a header',
      },
      create: { false: 'No', true: 'Yes' },
    },
    rows_one: '{{count, number}} row',
    rows_other: '{{count, number}} rows',
    valid: 'Valid pipeline',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitView: 'Fit view',
    minimap: 'Minimap',
    subtitle: 'Draw a pipeline, export its code: it runs outside graal.',
    new: 'New graph',
    newTitle: 'New graph',
    newSubtitle:
      'A graph describes a pipeline; the editor turns it into PySpark, Pandas, Polars or DuckDB SQL code.',
    created: 'Graph “{{name}}” created.',
    searchPlaceholder: 'Search a graph…',
    save: 'Save',
    saveDone: 'Graph saved.',
    saveFailed: 'Could not save.',
    delete: 'Delete graph',
    deleted: 'Graph “{{name}}” deleted.',
    deleteFailed: 'Deletion failed.',
    exported: '{{language}} code exported.',
    exportFailed: 'Code export failed.',
    exportUnsupported: 'Cannot export: node “{{node}}” — {{reason}}.',
    dagProblems: {
      unsupportedNode: 'this node type has no equivalent in the code generator',
      badCondition: 'unreadable condition (expected: column operator value, joined with “and”)',
      badJoin: 'missing join keys (expected: column, or left = right)',
      badAggregation: 'unreadable aggregations (expected: function(column), e.g. mean(consommation))',
      badPath: 'unrecognised path (expected: s3://bucket/path or /local/path)',
      badStructure:
        'unreadable structure near “{{detail}}” (expected: column:type, types str, int32, int64, double, bool)',
      badColumn: 'invalid column name “{{detail}}” (letters, digits and _ only)',
      badLiteral:
        'value “{{detail}}” rejected: the code generator does not escape quotes, backslashes and line breaks yet',
      missingStructure: 'an upstream read has no structure: fill in its columns and their types',
      unknownColumn: 'column “{{detail}}” is not in the data reaching this node',
      missingInput: 'this node is missing an input',
      tooManyInputs: 'this node has too many inputs',
      badNumber: 'number rejected “{{detail}}”',
      badOrder: 'unreadable order “{{detail}}” (expected: column asc or column desc)',
      badExpression: 'unreadable expression “{{detail}}” (columns, numbers, + - * / and parentheses)',
      badValue: 'replacement value rejected “{{detail}}” (expected: column = value, of the column type)',
      missingParam: 'parameter “{{detail}}” is required',
      badType: 'incompatible type for “{{detail}}”',
      badUnion: 'the inputs of a union must have the same columns',
      badHost: 'host rejected “{{detail}}” (host name or IPv4 address)',
      badPort: 'port rejected “{{detail}}” (integer from 1 to 65535)',
      badSecret:
        'field “{{detail}}”: a ${secret:<id>} reference is expected, never a value (and only one of password or private key)',
      badHostKey:
        'host key fingerprint rejected (expected: SHA256: followed by 43 characters, as ssh-keygen -lf)',
      badIdentifier: 'identifier rejected “{{detail}}” (letters, digits and _, 63 characters at most)',
      badUrl: 'address rejected “{{detail}}” (expected: https://host or https://host:port, without path)',
      badQuery: 'query parameter rejected “{{detail}}” (expected: name=value joined with &)',
      badCheck: 'unreadable check “{{detail}}”',
      badSetting: 'value rejected for “{{detail}}”',
      badPattern:
        'pattern rejected “{{detail}}”: outside the syntax shared by every engine (\\w, \\s, \\b, (?=…), backreferences rejected)',
      badBins: 'inconsistent ranges: increasing bounds, and one label per range',
    },
    connection: {
      label: 'Connection',
      help: 'Prefills the fields from a connection of project “{{project}}”. The DAG copies these values, it does not reference the connection.',
      help_unnamed:
        'Prefills the fields from a connection of this project. The DAG copies these values, it does not reference the connection.',
      placeholder: 'Choose a connection',
      loading: 'Loading the project connections…',
      none: 'No connection of this type in project “{{project}}”.',
      none_unnamed: 'No connection of this type in this project.',
      unavailable: 'Connections of project “{{project}}” unavailable.',
      unavailable_unnamed: 'Connections of this project unavailable.',
      applied: 'Fields prefilled from “{{name}}”.',
      types: {
        postgresql: 'PostgreSQL',
        mysql: 'MySQL',
        mariadb: 'MariaDB',
        sqlserver: 'SQL Server',
        oracle: 'Oracle',
        sftp: 'SFTP',
        hdfs: 'HDFS',
        http: 'REST API',
      },
    },
    selectNode: 'Select a node to see its parameters.',
    noParams: 'No parameter set',
    columns: {
      name: 'Name',
      owner: 'Owner',
      type: 'Type',
    },
    empty: {
      title: 'No graph yet',
      description: 'A graph is a drawn pipeline: sources, transforms, destinations.',
    },
    languages: {
      pyspark: 'PySpark',
      pandas: 'Pandas',
      polars: 'Polars',
      duckdb: 'DuckDB (SQL)',
    },
    problems: {
      empty: 'Empty canvas',
      noSource: 'No source',
      noSink: 'No destination',
      disconnected: 'A node is not connected',
    },
  },
  login: {
    tagline: 'Your data. Your infrastructure. Your agents.',
    title: 'Sign in',
    subtitle: 'Sign in to your organization’s workspace.',
    tenantLabel: 'Organization',
    tenantPlaceholder: 'e.g. energie-demo',
    tenantHint: 'Identifier provided by your administrator.',
    continue: 'Continue',
    redirecting: 'Redirecting to the identity provider…',
    platformAdmin: 'Platform administrator: sign in with my graal account',
    sso: 'Single sign-on (SSO) with your organization’s identity provider.',
    required: 'Enter your organization identifier.',
    invalid: 'Invalid identifier: lowercase letters, digits and dashes.',
    unknownTenant: 'Unknown organisation: check the identifier with your administrator.',
    unreachable: 'Authentication service unreachable. Try again in a moment.',
    hostedOn: 'Hosted on: {{infra}} · {{country}}',
    documentation: 'Documentation',
    privacy: 'Privacy',
    security: 'Security',
    version: 'Version {{version}}',
  },
  callback: {
    pending: 'Signing in…',
    failedTitle: 'Sign-in interrupted',
    failed: 'The identity provider did not confirm the sign-in. Start again from the sign-in page.',
    retry: 'Back to sign-in',
  },
  overview: {
    title: 'Overview',
    subtitle: 'Activity for project {{project}}.',
    subtitle_unnamed: 'Activity for this project.',
    runsTotal: 'Runs',
    runsRunning: 'Running',
    runsSucceeded: 'Succeeded',
    runsFailed: 'Failed',
    recent: 'Recent activity',
    empty: 'No run yet.',
    emptyHelp: 'Start a job or a workflow: its runs will show up here.',
    searchPlaceholder: 'Search a job…',
    viewJobs: 'View jobs',
    columns: {
      job: 'Job',
      run: 'Run',
      status: 'Status',
      triggeredBy: 'Triggered by',
      started: 'Started',
      duration: 'Duration',
    },
  },
  designLab: {
    title: 'Design lab',
    theme: 'Theme',
    mode: 'Mode',
    view: 'View',
    language: 'Language',
    recommended: 'recommended',
    views: {
      jobs: 'Jobs',
      run: 'Run + logs',
      palette: 'Palette ⌘K',
      agents: 'Agents ⌘J',
      editor: 'Editor',
      login: 'Login',
    },
  },
  mfa: {
    title: 'Two-factor',
    subtitle:
      'The second factor required at sign-in in the {{tenant}} organisation: for administrators, or for every member.',
    current: {
      OFF: 'No second factor is required.',
      ADMINS: 'Second factor required for administrators ({{method}}).',
      ALL: 'Second factor required for every member ({{method}}).',
    },
    administrators_one: '{{count}} administrator account is subject to it.',
    administrators_other: '{{count}} administrator accounts are subject to it.',
    enrolment:
      'An account without a configured second factor is asked to set one up at its next sign-in. A factor already configured is never removed.',
    fields: {
      policy: 'Policy',
      policyHelp: 'Who must use a second factor. Service accounts (agent tokens) are not affected.',
      method: 'Method',
      methodHelp: 'One-time code from an authenticator app (TOTP) or a security key / passkey (WebAuthn).',
    },
    policies: {
      OFF: 'Not required',
      ADMINS: 'Required for administrators',
      ALL: 'Required for everyone',
    },
    methods: {
      OTP: 'Authenticator app (TOTP)',
      WEBAUTHN: 'Security key or passkey (WebAuthn)',
    },
    save: 'Apply',
    saved: 'Two-factor policy applied to the realm.',
  },
  branding: {
    title: 'White label',
    subtitle:
      'The name, logo, main colour and sign-in theme of the {{tenant}} organisation, seen by all its members.',
    preview: 'Preview',
    sampleButton: 'Main button',
    logoAlt: 'Organisation logo',
    logoFailed: 'The logo does not load: the console keeps graal’s.',
    lowContrast:
      'Contrast of {{ratio}}:1 with the white text of buttons, below the {{min}}:1 minimum (WCAG AA): pick a darker colour.',
    saved: 'White label saved.',
    loginThemes: { graal: 'graal', keycloak: 'Keycloak (default)' },
    fields: {
      brandName: 'Brand name',
      brandNameHelp: 'Replaces “graal” in the sidebar (64 characters at most). Empty: graal.',
      brandNamePlaceholder: 'Energie Demo',
      logoUrl: 'Logo URL',
      logoUrlHelp: 'An https image, without credentials. If it does not load, the graal logo stays.',
      primaryColor: 'Main colour',
      primaryColorHelp: 'As #RRGGBB: the fill of the main button and of the sigil. Empty: graal’s.',
      loginTheme: 'Sign-in theme',
      loginThemeHelp: 'The sign-in page of the organisation’s Keycloak realm.',
      customDomain: 'Custom domain',
      customDomainHelp:
        'The host name the organisation serves the console on (no https://). It needs a DNS record pointing to graal and a certificate: see INSTALL.md §12.',
    },
  },
  security: {
    roles: {
      title: 'Roles',
      subtitle: 'RBAC roles of the {{tenant}} organisation: what each one may do, and on what.',
      searchPlaceholder: 'Search a role…',
      new: 'New role',
      newTitle: 'New role',
      newSubtitle:
        'A role groups permissions. It is then assigned to a user, a group or an agent, on a resource.',
      columns: {
        name: 'Name',
        type: 'Type',
        description: 'Description',
        holders: 'Holders',
        updated: 'Updated',
      },
      types: { internal: 'Built-in', custom: 'Custom' },
      fields: {
        name: 'Name',
        nameHelp: 'A readable label, unique in the organisation.',
        description: 'Description',
      },
      empty: { title: 'No role', description: 'Create a first role to organise permissions.' },
      created: 'Role “{{name}}” created.',
      deleted: 'Role “{{name}}” deleted.',
      deleteFailed: 'Deleting the role failed: {{detail}}',
      delete: 'Delete role',
      confirmDelete: 'Delete role “{{name}}”? Its assignments will be revoked.',
      detail: {
        subtitle_one: '{{type}} role · {{count}} holder',
        subtitle_other: '{{type}} role · {{count}} holders',
        tabs: { summary: 'Summary', holders: 'Holders' },
        permissions: 'Permissions',
        permissionsHint:
          'Patterns carried by this role. “/*” grants everything; “/job/*” grants every action on jobs.',
        noPermissions: 'This role carries no permission.',
        holdersHint: 'Who holds this role, and on which resource. Revoking removes the rights immediately.',
        identifier: 'Identifier',
        created: 'Created',
        updated: 'Updated',
      },
    },
    assignments: {
      title: 'Role assignments',
      subtitle: 'Who holds which role, on which resource — users, groups and agents.',
      searchPlaceholder: 'Search a principal, a role, a resource…',
      columns: { principal: 'Principal', role: 'Role', resource: 'Resource', since: 'Since' },
      principalTypes: { user: 'User', group: 'Group', identity: 'Agent', client: 'Token' },
      resourceTypes: {
        tenant: 'Organisation',
        project: 'Project',
        job: 'Job',
        workflow: 'Workflow',
        secret: 'Secret',
      },
      filters: { principalType: 'Principal', resourceType: 'Resource' },
      revoke: 'Revoke',
      confirmRevoke: 'Revoke role “{{role}}” from {{principal}}? Rights are removed immediately.',
      revoked: 'Role “{{role}}” revoked for {{principal}}.',
      revokeFailed: 'Revocation failed: {{detail}}',
      permissionsOf: 'Role permissions',
      showPermissions: 'Show permissions',
      empty: { title: 'No assignment', description: 'No role is assigned in this organisation.' },
      unknownPrincipal: 'Unknown principal',
      unknownRole: 'Unknown role',
    },
    audit: {
      title: 'Sign-ins',
      subtitle: 'Sign-in history of the {{tenant}} organisation — users and agents.',
      searchPlaceholder: 'Search a user, an address…',
      columns: { when: 'When', type: 'Event', user: 'Who', ip: 'IP address', location: 'Location' },
      types: { login: 'Sign-in', logout: 'Sign-out', login_error: 'Sign-in refused' },
      filters: { type: 'Event', kind: 'Principal' },
      empty: { title: 'No event', description: 'No sign-in has been recorded for this organisation yet.' },
      scopeNote: 'This screen covers sign-ins. The actions of humans and agents are in the audit trail.',
    },
    auditActions: {
      title: 'Audit trail',
      subtitle: 'The actions taken in the {{tenant}} organisation — by humans and by agents.',
      scopeNote:
        'One line per API read and write, and per refused right. Never a request body, a secret or a token.',
      logins: 'Sign-ins',
      loadMore: 'Load more',
      project: 'Project',
      columns: {
        when: 'When',
        who: 'Who',
        action: 'Action',
        resource: 'Resource',
        actor: 'Actor',
        kind: 'Access',
        result: 'Result',
        origin: 'Origin',
      },
      actors: { HUMAN: 'Human', AGENT: 'Agent', DEVICE: 'Device' },
      accessKinds: { READ: 'Read', WRITE: 'Write' },
      export: {
        label: 'Export',
        csv: 'Export as CSV',
        json: 'Export as JSON',
        done: 'Export saved: {{filename}}',
        failed: 'The export failed',
      },
      results: { ALLOWED: 'Allowed', DENIED: 'Denied' },
      periods: { '24h': 'Last 24 h', '7d': 'Last 7 days', '30d': 'Last 30 days', all: 'All time' },
      filters: {
        period: 'Period',
        actor: 'Human, agent or device',
        allActors: 'All actors',
        kind: 'Read or write',
        allKinds: 'Reads and writes',
        result: 'Result',
        allResults: 'All results',
        action: 'Action',
        actionPlaceholder: 'Action, e.g. /job/create',
        project: 'Project',
        projectPlaceholder: 'Project identifier',
        principal: 'Principal',
        principalPlaceholder: 'Principal identifier',
      },
      empty: {
        title: 'No action',
        description: 'No action matches these filters.',
      },
    },
    common: {
      unknownDetail: 'unknown error',
    },
  },
  access: {
    common: {
      identifier: 'Identifier',
      description: 'Description',
      created: 'Created',
      saved: 'Changes saved.',
      deleteFailed: 'Deletion failed: {{detail}}',
      deleteForbidden: 'Deletion refused: your rights do not cover it. Ask an organization administrator.',
      neverShown: 'Never shown again',
      copied: 'Copied to the clipboard.',
      copyFailed: 'Could not copy: select the text by hand.',
      copyLabel: 'Copy: {{what}}',
    },
    rights: {
      title: 'Rights',
      hint: 'The roles held by this principal, and on which resource. Revoking removes the right immediately.',
      empty: {
        title: 'No role',
        description: 'No role has been granted to this principal.',
      },
    },
    grant: {
      title: 'Grant a role',
      principalType: 'Grant to',
      types: { user: 'A user', group: 'A group', client: 'A token (agent or API)' },
      principals: { user: 'User', group: 'Group', client: 'Token' },
      clientOption: '{{name}} — {{kind}}',
      role: 'Role',
      roleHelp: 'The role’s permissions apply to this resource only.',
      submit: 'Grant',
      granted: 'Role granted.',
    },
    tokens: {
      title: 'Tokens & agents',
      subtitle:
        'Machine identities of the {{tenant}} organisation: agent tokens (MCP) and personal API tokens.',
      new: 'New token',
      searchPlaceholder: 'Search a token, a client id…',
      columns: { name: 'Name', kind: 'Kind', clientId: 'Client id', rights: 'Roles', created: 'Created' },
      kinds: { agent: 'Agent', api: 'Personal API' },
      kindHelp: {
        agent:
          'Agent token: it has no right until a role is granted to it. This is the identity to give an AI agent through MCP.',
        api: 'Personal API token: it acts with your own rights, without assignments of its own.',
      },
      inheritsShort: 'your rights',
      empty: {
        title: 'No token',
        description: 'Create an agent token to connect an AI agent (MCP) to this organisation.',
      },
      fields: {
        kind: 'Token kind',
        name: 'Name',
        nameHelp: 'A readable name: it shows in the audit log and in assignments.',
        description: 'Description',
        project: 'Project',
        projectHelp: 'The only project the agent will be able to act on.',
        role: 'Role',
        roleHelp: 'What the agent will be able to do on that project.',
      },
      wizard: {
        title: 'New token',
        subtitle: 'A machine identity for an AI agent or a script. Its secret is shown only once.',
        steps: { kind: 'Kind', identity: 'Identity', rights: 'Rights', review: 'Review' },
        rightsHelp:
          'The role is granted on this project right after creation. Without it, the agent could do nothing.',
        agentReserved:
          'An agent token creates an identity of the organisation: it requires the right to create identities, which you do not have. A personal API token is open to everyone.',
        submit: 'Create the token',
      },
      reveal: {
        heading: 'Token created',
        subtitle: 'Copy its secret now, then connect your agent.',
        title: 'The “{{name}}” token is created.',
        clientId: 'Client id (client_id)',
        secret: 'Client secret',
        copySecret: 'Copy the secret',
        once: 'This secret will never be shown again. If lost, delete the token and create another one.',
        noSecret: 'The server returned no secret for this token: delete it and create another one.',
        granted: 'Role “{{role}}” granted on project {{project}}.',
        grantFailed:
          'The token is created, but the role could not be granted: {{detail}}. Grant it from the token’s Rights tab.',
        done: 'I have copied the secret',
        doneHint: 'The secret leaves the screen.',
      },
      connect: {
        title: 'Connect an agent',
        step1: '1. Get an access token (paste the secret when the command waits for it: nothing is echoed)',
        step1Hint:
          'The access token expires after the organisation’s lifetime: to renew it, run this command again, then restart Claude Code from the same terminal.',
        step2: '2. Declare the MCP server in Claude Code (once)',
        step2Hint:
          'The single quotes matter: the configuration keeps the variable, not the token, and Claude Code reads it again at every start. The server relays the token it is given: the agent acts with that token’s rights, and only those.',
        noMcp: 'No MCP server address is configured on this installation (GRAAL_MCP_URL).',
      },
      detail: {
        subtitle: '{{kind}} token · created {{when}}',
        tabs: { summary: 'Summary', rights: 'Rights', definition: 'Definition' },
        owner: 'Created by',
        secretNote: 'Its secret is never shown again: if lost, delete the token and create another one.',
        rightsHint:
          'This agent’s roles. Revoking removes the right on the agent’s next request — this is what act 7b shows.',
        grant: 'Grant a role',
        grantTitle: 'Grant a role on a project',
        delete: 'Delete the token',
        confirmDelete: 'Delete the “{{name}}” token? Any agent using it loses access immediately.',
        deleted: 'Token “{{name}}” deleted.',
      },
    },
    secrets: {
      title: 'Secrets',
      subtitle:
        'Sensitive values of the {{tenant}} organisation, encrypted at rest. A secret is typed or replaced; it is never read back.',
      new: 'New secret',
      searchPlaceholder: 'Search a secret…',
      columns: { name: 'Name', description: 'Description', labels: 'Labels', updated: 'Updated' },
      empty: {
        title: 'No secret',
        description: 'Declare a password or a key here rather than in a job’s options.',
      },
      fields: {
        name: 'Name',
        nameHelp:
          'The key a job reads (unseal.properties file): letters, digits, “_”, “.” and “-”, no space.',
        description: 'Description',
        value: 'Value',
        newValue: 'New value',
        valueHelp: 'Masked, single line. It will never be shown again.',
      },
      newTitle: 'New secret',
      newSubtitle: 'The value is encrypted before it is written, and will never be shown again.',
      created: 'Secret “{{name}}” created.',
      detail: {
        subtitle: 'Secret · updated {{when}}',
        tabs: { summary: 'Summary', definition: 'Definition', value: 'Value', access: 'Access' },
        value: 'Value',
        sealed: 'Encrypted — never shown again',
        usage:
          'A job reads this secret in /workspace/secrets/unseal.properties, under its name. Its value goes neither through the console nor through the job’s options.',
        renameHint: 'Renaming a secret changes the key jobs read: update them.',
        rotateHint:
          'Replace the value without ever reading the old one. The next runs will read the new value.',
        rotate: 'Replace the value',
        managedKey:
          'Key of the managed bucket “{{bucket}}”: the platform sets it and keeps it in line with its S3 user. Its value cannot be replaced here.',
        managedKey_unnamed:
          'Key of a managed bucket: the platform sets it and keeps it in line with its S3 user. Its value cannot be replaced here.',
        rotated: 'Value of secret “{{name}}” replaced.',
        accessTitle: 'Access',
        accessHint:
          'Who can read or change this secret. A secret belongs to the organisation: access is granted by role, secret by secret.',
        accessEmpty: {
          title: 'No access granted',
          description: 'Only the organisation’s administrators can read this secret.',
        },
        delete: 'Delete the secret',
        confirmDelete: 'Delete the “{{name}}” secret? Jobs that read it will no longer find it.',
        deleted: 'Secret “{{name}}” deleted.',
      },
    },
    libraries: {
      title: 'Libraries',
      subtitle: 'Code uploaded for the jobs of the {{tenant}} organisation: wheels, requirements, JARs.',
      scopeNote: 'Visible to the whole organisation: no per-library right is enforced yet.',
      searchPlaceholder: 'Search a file…',
      upload: 'Upload',
      uploadInput: 'Files to upload',
      uploaded_one: '{{count}} file uploaded.',
      uploaded_other: '{{count}} files uploaded.',
      uploadFailed: 'Upload failed: {{detail}}',
      columns: {
        filename: 'File',
        size: 'Size',
        type: 'Type',
        key: 'Identifier',
        modified: 'Uploaded',
      },
      download: 'Download {{name}}',
      downloadFailed: 'Download failed: {{detail}}',
      delete: 'Delete the library',
      deleteLabel: 'Delete {{name}}',
      confirmDelete: 'Delete “{{name}}”? Jobs that install it will fail on their next run.',
      deleted: 'Library “{{name}}” deleted.',
      empty: {
        title: 'No library',
        description: 'Upload a Python package (.whl), a requirements.txt or a JAR.',
      },
    },
    members: {
      title: 'Members',
      subtitle: 'The users of the {{tenant}} organisation.',
      new: 'New user',
      searchPlaceholder: 'Search a name, a username, an address…',
      columns: {
        name: 'Name',
        username: 'Username',
        email: 'Email address',
        origin: 'Origin',
        created: 'Joined',
      },
      locked: 'Locked',
      empty: { title: 'No user', description: 'Create the organisation’s first user.' },
      fields: {
        username: 'Username',
        usernameHelp: 'Lowercase letters, digits, “.”, “_” and “-”: this is the sign-in name.',
        firstname: 'First name',
        lastname: 'Last name',
        email: 'Email address',
        description: 'Description',
        password: 'Initial password',
        passwordHelp: 'At least {{min}} characters. It is never shown again.',
        confirm: 'Confirm the password',
      },
      errors: {
        username: 'Lowercase letters, digits, “.”, “_” and “-”; 2 to 63 characters.',
        email: 'Invalid email address.',
        passwordLength: 'At least {{min}} characters.',
        mismatch: 'The two passwords differ.',
      },
      newTitle: 'New user',
      newSubtitle: 'The user is created in the organisation’s directory, with an initial password.',
      created: 'User “{{name}}” created.',
      detail: {
        tabs: { summary: 'Summary', definition: 'Definition', rights: 'Rights', activity: 'Activity' },
        activityTitle: 'Sign-ins',
        delete: 'Delete the user',
        confirmDelete: 'Delete the user “{{name}}”? They will no longer be able to sign in.',
        deleted: 'User “{{name}}” deleted.',
      },
    },
    groups: {
      title: 'Groups',
      subtitle: 'The groups of the {{tenant}} organisation.',
      new: 'New group',
      searchPlaceholder: 'Search a group…',
      columns: { name: 'Name', description: 'Description', roles: 'Roles', created: 'Created' },
      empty: { title: 'No group', description: 'Create a group to gather users.' },
      fields: {
        name: 'Name',
        nameHelp: 'A short name, unique within the organisation.',
        description: 'Description',
      },
      newTitle: 'New group',
      newSubtitle: 'A group receives roles, like a user.',
      created: 'Group “{{name}}” created.',
      detail: {
        tabs: { summary: 'Summary', rights: 'Rights' },
        membersNote:
          'Group members cannot be managed from the console yet: no membership operation is exposed, and a group’s roles do not apply to its members yet.',
        delete: 'Delete the group',
        confirmDelete: 'Delete the group “{{name}}”?',
        deleted: 'Group “{{name}}” deleted.',
      },
    },
    identityProviders: {
      title: 'Identity providers',
      subtitle:
        'Sign-in of the {{tenant}} organisation’s users through an external directory, federated in its Keycloak realm.',
      new: 'New provider',
      searchPlaceholder: 'Search a provider…',
      columns: { name: 'Name', type: 'Provider', clientId: 'Client id', created: 'Created' },
      types: {
        microsoft: 'Microsoft Entra ID',
        google: 'Google',
        github: 'GitHub',
        gitlab: 'GitLab',
        'linkedin-openid-connect': 'LinkedIn',
        oidc: 'OpenID Connect',
        'keycloak-oidc': 'Keycloak (OpenID Connect)',
        saml: 'SAML 2.0',
        ldap: 'LDAP directory',
        'active-directory': 'Active Directory',
      },
      nameIdFormats: {
        persistent: 'Persistent',
        transient: 'Transient',
        emailAddress: 'Email address',
        unspecified: 'Unspecified',
      },
      principalTypes: {
        SUBJECT: 'Subject (NameID)',
        ATTRIBUTE: 'Attribute',
        FRIENDLY_ATTRIBUTE: 'Attribute (friendly name)',
      },
      searchScopes: { subtree: 'Whole subtree', one: 'One level' },
      empty: {
        title: 'No provider',
        description:
          'Federate the organisation’s directory (Entra ID, OpenID Connect, SAML, LDAP, Active Directory…) for sign-in.',
      },
      fields: {
        name: 'Name',
        nameHelp: 'The label of the sign-in button.',
        description: 'Description',
        type: 'Provider',
        clientId: 'Client id',
        clientIdHelp: 'The id of the application registered with the provider.',
        clientSecret: 'Client secret',
        clientSecretHelp: 'Masked. It is never shown again.',
        typeHelp:
          'A social provider, OpenID Connect or SAML (brokering), or an LDAP / Active Directory directory (read-only federation).',
        discoveryUrl: 'Discovery URL',
        discoveryUrlHelp:
          'The provider’s .well-known/openid-configuration document. Otherwise, the authorization and token URLs.',
        authorizationUrl: 'Authorization URL',
        tokenUrl: 'Token URL',
        metadataUrl: 'Metadata URL',
        metadataUrlHelp: 'The IdP’s SAML descriptor. Otherwise, the SSO URL and the entity id.',
        ssoUrl: 'SSO URL',
        entityId: 'IdP entity id',
        signingCertificate: 'Signing certificate',
        signingCertificateHelp: 'The X.509 certificate (PEM or base64) that verifies the IdP’s signatures.',
        nameIdFormat: 'NameID format',
        principalType: 'User identifier',
        principalTypeHelp: 'What identifies the user in the SAML assertion.',
        principalAttribute: 'User attribute',
        connectionUrl: 'Connection URL',
        connectionUrlHelp: 'ldap:// or ldaps://. The connection is tested before creation.',
        usersDn: 'Users DN',
        bindDn: 'Bind DN',
        bindDnHelp:
          'The service account that reads the directory. Without it and a password: anonymous bind.',
        bindCredential: 'Bind password',
        usernameAttribute: 'Username attribute',
        usernameAttributeHelp: 'uid for LDAP, sAMAccountName for Active Directory, when left empty.',
        searchScope: 'Search scope',
        secretKeepHelp: 'Masked. Leave empty to keep the current value, never shown again.',
      },
      newTitle: 'New identity provider',
      newSubtitle: 'Register an application with the provider first; its redirect URI is then shown here.',
      created: 'Provider “{{name}}” created.',
      detail: {
        tabs: { summary: 'Summary', definition: 'Definition' },
        redirectUri: 'Redirect URI',
        redirectUriHelp: 'Register it in the provider’s application, as is.',
        configuration: 'Configuration',
        configurationEmpty: 'No configuration of its own: that of Keycloak’s social provider.',
        federationNote:
          'Read-only user federation: directory accounts sign in with their usual password, without a redirect.',
        credentialsNote:
          'The id, the secret and the configuration are changed in the Definition tab and carried to Keycloak; the type cannot change: delete the provider and declare it again.',
        delete: 'Delete the provider',
        confirmDelete:
          'Delete the “{{name}}” provider? Its users will no longer be able to sign in through it.',
        deleted: 'Provider “{{name}}” deleted.',
      },
    },
    projectMembers: {
      title: 'Project members',
      short: 'Members',
      subtitle: 'Who has access to project {{project}}, and with which role — users, groups and agents.',
      subtitle_unnamed: 'Who has access to this project, and with which role — users, groups and agents.',
      searchPlaceholder: 'Search a member, a role…',
      add: 'Add a member',
      addTitle: 'Add a member',
      addSubtitle: 'Grant a role on project {{project}}.',
      addSubtitle_unnamed: 'Grant a role on this project.',
      tenantMembers: 'Organisation members',
      empty: {
        title: 'No member',
        description: 'Nobody holds a role on this project yet.',
      },
    },
  },
  data: {
    common: {
      yes: 'Yes',
      no: 'No',
      unknownDetail: 'unknown error',
      identifier: 'Identifier',
      description: 'Description',
      labels: 'Labels',
      created: 'Created',
      updated: 'Updated',
      owner: 'Owner',
      saved: 'Changes saved.',
    },
    buckets: {
      title: 'Buckets',
      subtitle: 'Object storage declared to the {{tenant}} organization: where jobs read and write.',
      searchPlaceholder: 'Search buckets…',
      columns: {
        name: 'Name',
        type: 'Type',
        location: 'Location',
        description: 'Description',
        updated: 'Updated',
      },
      types: {
        s3: 'S3',
        managed: 'Managed',
        azure: 'Azure',
        gcp: 'GCP',
        generic: 'Generic',
        unknown: 'Other',
      },
      filters: { type: 'Type' },
      empty: {
        title: 'No buckets',
        description: 'No bucket is declared for this organization.',
      },
      detail: {
        subtitle: '{{type}} bucket · {{location}}',
        tabs: { files: 'Files', summary: 'Summary' },
        location: 'Location',
        endpoint: 'Endpoint',
        region: 'Region',
        infrastructure: 'Infrastructure',
        scope: 'Scope',
        locked: 'Locked',
        credentialsNote:
          'Access credentials are kept in the organization secret store; the console never displays them.',
      },
      files: {
        root: 'Root',
        breadcrumb: 'Location in the bucket',
        parent: 'Parent folder',
        title: 'Contents',
        columns: { name: 'Name', size: 'Size', type: 'Content type', modified: 'Modified' },
        searchPlaceholder: 'Search this folder…',
        upload: 'Upload files',
        uploadInput: 'Files to upload',
        uploaded_one: '{{count}} file uploaded to {{path}}.',
        uploaded_other: '{{count}} files uploaded to {{path}}.',
        uploadFailed: 'Upload failed: {{detail}}',
        download: 'Download {{name}}',
        downloadFailed: 'Download failed: {{detail}}',
        delete: 'Delete {{name}}',
        deleteTitle: 'Delete file',
        confirmDelete: 'Delete “{{path}}”? The object is erased from the bucket, with no recycle bin.',
        deleted: '“{{name}}” deleted.',
        deleteFailed: 'Deletion failed: {{detail}}',
        truncated:
          'This folder holds at least {{count}} entries: the server returns no more, the listing may be incomplete.',
        empty: { title: 'Empty folder', description: 'Upload a first file to this folder.' },
      },
    },
    catalog: {
      title: 'Catalog',
      subtitle: 'The {{tenant}} organization data, organized in catalogs, databases, tables and fields.',
      levels: { layer: 'Catalog', database: 'Database', table: 'Table', field: 'Field' },
      children: { layer: 'Databases', database: 'Tables', table: 'Fields' },
      columns: {
        name: 'Name',
        technicalName: 'Technical name',
        description: 'Description',
        type: 'Type',
        format: 'Format',
        nullable: 'Nullable',
        location: 'Location',
        updated: 'Updated',
      },
      search: {
        layer: 'Search catalogs…',
        database: 'Search databases…',
        table: 'Search tables…',
        field: 'Search fields…',
      },
      new: {
        layer: 'New catalog',
        database: 'New database',
        table: 'New table',
        field: 'New field',
      },
      newSubtitle: {
        layer:
          'A catalog groups databases by how prepared their data is (raw, curated…) or by engine (iceberg).',
        database: 'A database groups the tables of a domain, in the {{parent}} catalog.',
        table: 'A table describes a stored dataset, in the {{parent}} database.',
        field: 'A field is a column of the {{parent}} table.',
      },
      empty: {
        layer: {
          title: 'No catalogs',
          description: 'Create a first catalog (raw, curated…) to organize the data.',
        },
        database: { title: 'No databases', description: 'This catalog holds no database yet.' },
        table: { title: 'No tables', description: 'This database holds no table yet.' },
        field: { title: 'No fields', description: 'The schema of this table is not declared yet.' },
      },
      created: '“{{name}}” added to the catalog.',
      detail: {
        tabs: { summary: 'Summary', definition: 'Definition' },
      },
      delete: {
        layer: 'Delete catalog',
        database: 'Delete database',
        table: 'Delete table',
        field: 'Delete field',
      },
      confirmDelete: {
        layer: 'Delete the “{{name}}” catalog? Its databases, tables and fields are deleted with it.',
        database: 'Delete the “{{name}}” database? Its tables and their fields are deleted with it.',
        table: 'Delete the “{{name}}” table? Its fields are deleted with it.',
        field: 'Delete the “{{name}}” field?',
      },
      deleted: '“{{name}}” removed from the catalog.',
      deleteFailed: 'Deletion failed: {{detail}}',
      fields: {
        technicalName: 'Technical name',
        technicalNameHelp:
          'Key in URLs and SQL engines: lowercase letters, digits and “_”. It cannot change afterwards.',
        name: 'Label',
        nameHelp: 'The displayed name; defaults to the technical name.',
        description: 'Description',
        uri: 'Location',
        uriHelp: 'Storage prefix of the table, for instance s3://energie-demo/curated/conso/.',
        tableType: 'Table type',
        tableTypeHelp: 'As the engine declares it (file for files on object storage).',
        format: 'Format',
        fieldType: 'Type',
        fieldTypeHelp: 'Column type, as the engine writes it (string, double, int64, date32[day]…).',
        nullable: 'Null values allowed',
      },
    },
    warehouses: {
      title: 'SQL warehouses',
      subtitle: 'The SQL warehouses of the {{tenant}} organization.',
      searchPlaceholder: 'Search warehouses…',
      columns: {
        name: 'Name',
        engine: 'Engine',
        status: 'Status',
        version: 'Version',
        size: 'Size',
        owner: 'Owner',
        updated: 'Updated',
      },
      engines: {
        trino: 'Trino',
        postgresql: 'PostgreSQL',
        mysql: 'MySQL',
        mariadb: 'MariaDB',
        oracle: 'Oracle',
        sqlserver: 'SQL Server',
        db2: 'IBM DB2',
        snowflake: 'Snowflake',
        unknown: 'Other engine',
      },
      kinds: { managed: 'Managed', external: 'External', unknown: 'Other' },
      statuses: {
        running: 'Running',
        stopped: 'Stopped',
        pending: 'Starting',
        starting: 'Starting',
        failed: 'Failed',
        unknown: 'Unknown',
      },
      workers_one: '{{count}} worker',
      workers_other: '{{count}} workers',
      filters: { status: 'Status' },
      empty: {
        title: 'No warehouses',
        description: 'No SQL warehouse is declared for this organization.',
      },
      detail: {
        subtitle: '{{engine}} {{version}} · {{kind}}',
        tabs: { summary: 'Summary', tables: 'Tables', changes: 'Migrations', logs: 'Log', costs: 'Costs' },
        engine: 'Engine',
        kind: 'Mode',
        version: 'Version',
        instanceType: 'Instance type',
        workers: 'Workers',
        infrastructure: 'Infrastructure',
        identity: 'Execution identity',
        publicUrl: 'URL',
        conf: 'Configuration',
        env: 'Environment variables',
        credentialsNote: 'The warehouse connection credentials are never displayed by the console.',
        query: 'Query',
      },
      tables: {
        title: 'Exposed tables',
        description: 'What the warehouse engine sees, read on demand.',
        column: 'Table',
        searchPlaceholder: 'Search tables…',
        empty: { title: 'No tables', description: 'The engine exposes no table yet.' },
      },
      changes: {
        title: 'Migrations',
        description: 'Schema scripts applied to the warehouse, in order.',
        columns: {
          order: 'Order',
          filename: 'File',
          description: 'Description',
          author: 'Author',
          executed: 'Executed',
          tag: 'Tag',
        },
        searchPlaceholder: 'Search migrations…',
        empty: { title: 'No migrations', description: 'No schema script has been applied yet.' },
      },
      logs: { empty: 'No log line for this warehouse.' },
    },
    sql: {
      title: 'SQL query',
      subtitle: 'Read-only on the {{name}} warehouse: one read statement, at most {{limit}} rows.',
      scopeTenant:
        'This Trino warehouse covers the whole tenant: it reads every Iceberg table of the tenant, whatever the project. Catalog read access comes from the Catalog Reader role.',
      fields: {
        sql: 'Query',
        sqlHelp: 'A single SELECT, WITH, SHOW TABLES, DESCRIBE or EXPLAIN statement; writes are refused.',
        limit: 'Maximum rows',
      },
      run: 'Run',
      result: 'Result',
      resultMeta_one: '{{count}} row in {{ms}} ms',
      resultMeta_other: '{{count}} rows in {{ms}} ms',
      truncated: 'Result cut at {{limit}} rows.',
      empty: { title: 'No rows', description: 'The query returns no row.' },
      idle: { title: 'No query run yet', description: 'Write a query, then run it.' },
    },
  },
  sessions: {
    title: 'Sessions',
    subtitle: 'Interactive Python kernels: each cell runs in a workspace, relayed by the platform.',
    searchPlaceholder: 'Search a session…',
    columns: {
      name: 'Name',
      kernel: 'Kernel',
      project: 'Project',
      used: 'Last run',
      status: 'State',
      workspace: 'Workspace',
    },
    new: 'New session',
    newTitle: 'New session',
    newSubtitle: 'A Python kernel in its own workspace: counted in the quota, stopped when idle.',
    detailSubtitle: 'Kernel {{kernel}} — variables survive from one cell to the next.',
    empty: {
      title: 'No session',
      description: 'Open a session to explore data in Python, cell by cell.',
    },
    fields: {
      name: 'Name',
      kernel: 'Kernel',
      kernelHelp: 'Empty: python3.',
      infrastructure: 'Infrastructure',
      instanceType: 'Size',
      project: 'Project',
      projectHelp: 'Optional: the members who edit the project will also be able to run code in the session.',
      noProject: 'No project',
    },
    created: 'Session “{{name}}” created: the kernel is starting.',
    delete: 'Delete session',
    confirmDelete:
      'Delete session “{{name}}”? Its kernel and variables are lost; data in buckets and tables stays.',
    deleted: 'Session “{{name}}” deleted.',
    deleteFailed: 'Deletion failed: {{detail}}',
    cell: {
      title: 'Cells',
      code: 'Cell code',
      placeholder: 'import pandas as pd',
      input: 'In [{{index}}]',
      run: 'Run',
      hint: 'Ctrl+Enter to run.',
      startingHint: 'The kernel is starting: the first cell may wait a minute.',
      starting: 'The kernel is still starting: try again in a few seconds.',
      failed: 'The run failed: {{detail}}',
      noOutput: 'No output.',
      meta: '{{status}} · execution {{count}} · {{duration}} ms',
      kinds: { stdout: 'Output', stderr: 'Errors', result: 'Result', error: 'Exception' },
    },
  },
  ml: {
    common: {
      identifier: 'Identifier',
      project: 'Project',
      owner: 'Owner',
      labels: 'Labels',
      description: 'Description',
      created: 'Created',
      updated: 'Updated',
      saved: 'Changes saved.',
      unknownDetail: 'unknown error',
    },
    fields: {
      name: 'Name',
      nameHelp: {
        experiment: 'The name the training job logs its runs under.',
        model: 'The model name in the organisation’s registry.',
      },
      description: 'Description',
    },
    experiments: {
      title: 'Experiments',
      subtitle: 'Experiment tracking for project {{project}}: runs, parameters and metrics.',
      subtitle_unnamed: 'Experiment tracking for this project: runs, parameters and metrics.',
      searchPlaceholder: 'Search an experiment…',
      columns: {
        name: 'Name',
        description: 'Description',
        labels: 'Labels',
        owner: 'Owner',
        updated: 'Updated',
      },
      new: 'New experiment',
      newTitle: 'New experiment',
      newSubtitle: 'An experiment groups the runs of one problem in project {{project}}.',
      newSubtitle_unnamed: 'An experiment groups the runs of one problem in this project.',
      empty: {
        title: 'No experiment',
        description: 'Create an experiment, then log a training job’s runs into it.',
      },
      created: 'Experiment “{{name}}” created.',
      delete: 'Delete experiment',
      confirmDelete: 'Delete “{{name}}”? Its runs, their parameters and their metrics are erased.',
      deleted: 'Experiment “{{name}}” deleted.',
      deleteFailed: 'Deletion failed: {{detail}}',
      detail: {
        subtitle: 'Experiment of project {{project}} · {{owner}}',
        subtitle_unnamed: 'Experiment of this project · {{owner}}',
        tabs: { runs: 'Runs', summary: 'Summary', definition: 'Definition', permissions: 'Permissions' },
        compare: 'Compare runs',
        trackedMetrics: 'Tracked metrics',
        trackedMetricsHint:
          'Keys logged by at least one run of the experiment, and their direction when known.',
        noMetrics: 'No run has logged a metric yet.',
        permissionsHint:
          'Rights on an experiment are given by role assignment: a user, a group or an agent receives a role on this experiment or on its project.',
        grantRoles: 'Manage assignments',
      },
    },
    runs: {
      title: 'Runs',
      description: 'Each run logs its parameters and metrics. Open one, or compare up to three.',
      searchPlaceholder: 'Search a run…',
      columns: {
        name: 'Name',
        status: 'Status',
        description: 'Description',
        duration: 'Duration',
        start: 'Started',
      },
      filters: { status: 'Status' },
      empty: {
        title: 'No run',
        description: 'Runs appear when a training job logs its parameters and metrics.',
      },
      delete: 'Delete run',
      confirmDelete: 'Delete run “{{name}}”? Its parameters and metrics are erased.',
      deleted: 'Run “{{name}}” deleted.',
      deleteFailed: 'Deletion failed: {{detail}}',
      detail: {
        subtitle: 'Run of experiment {{experiment}}',
        tabs: { metrics: 'Metrics', params: 'Parameters', summary: 'Summary' },
        compare: 'Compare',
        register: 'Register a version',
        registerTo: 'In model…',
        noModel: 'No model in the registry',
        experiment: 'Experiment',
        status: 'Logged status',
        duration: 'Duration',
        start: 'Start',
        end: 'End',
        artifactUri: 'Artifact location',
        jobRun: 'Job run',
        noJobRun: 'None: run logged outside a graal job.',
      },
      metrics: {
        empty: {
          title: 'No metric',
          description: 'This run logged no metric.',
          running: 'This run is in progress: its metrics will appear as training goes.',
        },
        allValues: 'All values',
        allValuesDescription: 'Every logged point, by metric and step.',
        searchPlaceholder: 'Search a metric…',
        columns: { key: 'Metric', step: 'Step', value: 'Value', time: 'Logged at' },
      },
      params: {
        title: 'Parameters',
        description: 'The hyperparameters logged by the run, as is.',
        searchPlaceholder: 'Search a parameter…',
        columns: { key: 'Parameter', value: 'Value' },
        empty: { title: 'No parameter', description: 'This run logged no parameter.' },
      },
    },
    compare: {
      crumb: 'Comparison',
      title: 'Compare runs',
      subtitle: 'Experiment {{experiment}}: up to {{max}} runs, side by side.',
      picker: 'Compared runs',
      pickerHint:
        'Up to {{max}} runs. Each keeps its colour and marker shape, in the charts and in the legend.',
      none: { title: 'No run selected', description: 'Pick at least one run above.' },
      best: 'best',
      direction: { min: 'lower is better', max: 'higher is better' },
      metrics: {
        title: 'Metrics',
        description:
          'The last value of each metric. The best run is marked when the metric’s direction is known.',
        searchPlaceholder: 'Search a metric…',
        columns: { key: 'Metric' },
        empty: { title: 'No metric', description: 'The selected runs logged no metric.' },
      },
      charts: {
        title: 'Curves',
        description:
          'One metric per chart. Hover, or use the arrow keys, to read each run’s value at a step.',
      },
      params: {
        title: 'Parameters',
        description: 'The hyperparameters of the selected runs. Filter on the values that differ.',
        searchPlaceholder: 'Search a parameter…',
        columns: { key: 'Parameter' },
        filter: 'Values',
        different: 'Different',
        same: 'Identical',
        empty: { title: 'No parameter', description: 'The selected runs logged no parameter.' },
      },
    },
    charts: {
      linesCaption: 'by training step',
      barsCaption: 'logged value',
      legend: 'Legend',
      step: 'step {{step}}',
      noPoints: 'No point logged.',
      summary: '{{metric}}, last value per run: {{values}}.',
    },
    models: {
      title: 'Models',
      subtitle: 'The model registry of organisation {{tenant}}: versions and stages.',
      searchPlaceholder: 'Search a model…',
      columns: { name: 'Name', description: 'Description', labels: 'Labels', updated: 'Updated' },
      new: 'New model',
      newTitle: 'New model',
      newSubtitle: 'A model groups the successive versions of one predictor; each has its stage.',
      fields: {
        project: 'Project',
        projectHelp:
          'The owning project: its members who edit its experiments manage this model. It cannot change afterwards.',
      },
      empty: {
        title: 'No model',
        description: 'Register a model, then its versions from your experiments’ runs.',
      },
      created: 'Model “{{name}}” created.',
      delete: 'Delete model',
      confirmDelete: 'Delete model “{{name}}” and all its versions? Artifacts are erased.',
      deleted: 'Model “{{name}}” deleted.',
      deleteFailed: 'Deletion failed: {{detail}}',
      detail: {
        subtitle: 'Model of the {{tenant}} registry',
        tabs: { versions: 'Versions', summary: 'Summary', definition: 'Definition' },
        production: 'Version {{version}} in production',
        noProduction: 'No version in production',
        productionVersion: 'Production version',
        versionCount: 'Versions',
      },
    },
    versions: {
      title: 'Versions',
      description: 'Each version points to an artifact and, when it comes from one, to its experiment run.',
      label: 'v{{name}}',
      searchPlaceholder: 'Search a version…',
      columns: {
        name: 'Version',
        stage: 'Stage',
        run: 'Source run',
        description: 'Description',
        created: 'Created',
      },
      filters: { stage: 'Stage' },
      empty: {
        title: 'No version',
        description: 'Create a version, or register one from a run’s detail.',
      },
      new: 'New version',
      newTitle: 'New version of {{model}}',
      newSubtitle: 'The version starts at stage “None”; change it afterwards from its detail.',
      fromRun: 'From run {{run}} of experiment {{experiment}}.',
      fields: {
        name: 'Version number',
        nameHelp: 'Letters, digits, “.”, “_” and “-”. Defaults to the next number.',
        source: 'Artifact source',
        sourceHelp: 'URI of the model artifact (s3://…, mlflow_artifact:/…). Optional.',
      },
      created: 'Version {{name}} created.',
      delete: 'Delete version',
      confirmDelete: 'Delete version {{name}} of model {{model}}? Its artifact is erased.',
      deleted: 'Version {{name}} deleted.',
      deleteFailed: 'Deletion failed: {{detail}}',
      detail: {
        subtitle: 'Version of model {{model}}',
        tabs: { summary: 'Summary', artifact: 'Artifact' },
        run: 'Source run',
        noRun: 'No source run',
        experiment: 'Source experiment',
        source: 'Source',
      },
      artifact: {
        filename: 'File',
        size: 'Size',
        contentType: 'Content type',
        key: 'Storage key',
        lastModified: 'Modified',
        download: 'Download {{name}}',
        downloadFailed: 'Download failed: {{detail}}',
      },
    },
    stages: {
      field: 'Stage',
      values: { None: 'None', Staging: 'Staging', Production: 'Production', Archived: 'Archived' },
      change: 'Change stage',
      changeTitle: 'Change the stage of version {{name}}',
      changeDescription: 'The stage says what the version is for: trial, staging, production, archive.',
      archiveExisting: 'Archive the other production versions',
      archiveExistingHelp: 'The model then has a single version in production.',
      submit: 'Change stage',
      changed: 'Version {{name}}: {{stage}}.',
      archived_one: '{{count}} other version archived.',
      archived_other: '{{count}} other versions archived.',
    },
  },
  // Lot L5 — administration and execution: texts in `admin.en.ts`.
  admin: adminEn,
  secretStore: secretStoreEn,
  // 2026-10-06 — social feed (#52) and low-code template gallery (#54): `social.en.ts`.
  ai: aiEn,
  social: socialEn,
  // 2026-10-10 — lot L01, billing (invoices, transactions, payment methods): `billing.en.ts`.
  billing: billingEn,
  gallery: galleryEn,
  // 2026-10-10 — global search (ADR 0043): `search.en.ts`.
  search: searchEn,
  // 2026-10-10 — usage CPU et mémoire (ADR 0053) : `usage.en.ts`.
  resourceUsage: resourceUsageEn,
  // 2026-10-10 — orchestration (retries, catch-up, backfills, triggers): `orchestration.en.ts`.
  orchestration: orchestrationEn,
  // 2026-11-03 — éditeur de graphe des workflows, lancement avec paramètres, run (ADR 0102).
  workflowEditor: workflowEditorEn,
  // 2026-10-19 — alertes d'un job (webhooks signés, derniers envois) : `alerts.en.ts`.
  alerts: alertsEn,
  // Lot L07 — connections, lineage, quality, step preview, catalog search: `donnees.en.ts`.
  connections: connectionsEn,
  lineage: lineageEn,
  quality: qualityEn,
  preview: previewEn,
  catalogSearch: catalogSearchEn,
  catalogTable: catalogTableEn,
  glossary: glossaryEn,
  // 2026-10-23 — gaps with the Angular console: `gestes.en.ts`.
  projectSettings: projectSettingsEn,
  projectMetrics: projectMetricsEn,
  bucketManage: bucketManageEn,
  warehouseManage: warehouseManageEn,
  managedWarehouse: managedWarehouseEn,
  preferences: preferencesEn,
  // 2026-10-30 — file preview (buckets, libraries): `apercu-fichier.en.ts`.
  filePreview: filePreviewEn,
  // 2026-11-04 — references between objects (name + link): `references.en.ts`.
  references: referencesEn,
};
